import { useState, useEffect, useCallback } from "react";
import GeneratorTab from "./GeneratorTab";
import CoachTab from "./CoachTab";
import ScaleNotation from "./ScaleNotation";
import ChordNetwork from "./ChordNetwork";
import HarmonyMap from "./HarmonyMap";
import { AuthProvider, useAuth } from "./AuthContext";
import { getProgress, saveDay, deleteDay, getPieces, createPiece, updatePiece, deletePiece, getPieceSessions, getSessionDates, getSessionsForDay, createSession, updateSession, deleteSession } from "./api";
import LoginScreen from "./LoginScreen";
import ProfileModal from "./ProfileModal";

// ─── Data ─────────────────────────────────────────────────────────────────────

const CIRCLE = [
  { key:"C",  major:"C",  minor:"Am",  sharps:0  },
  { key:"G",  major:"G",  minor:"Em",  sharps:1  },
  { key:"D",  major:"D",  minor:"Bm",  sharps:2  },
  { key:"A",  major:"A",  minor:"F#m", sharps:3  },
  { key:"E",  major:"E",  minor:"C#m", sharps:4  },
  { key:"B",  major:"B",  minor:"G#m", sharps:5  },
  { key:"F#", major:"F#", minor:"D#m", sharps:6  },
  { key:"Db", major:"Db", minor:"Bbm", sharps:-5 },
  { key:"Ab", major:"Ab", minor:"Fm",  sharps:-4 },
  { key:"Eb", major:"Eb", minor:"Cm",  sharps:-3 },
  { key:"Bb", major:"Bb", minor:"Gm",  sharps:-2 },
  { key:"F",  major:"F",  minor:"Dm",  sharps:-1 },
];

const NOTES_SHARP = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
const NOTES_FLAT  = ["C","Db","D","Eb","E","F","Gb","G","Ab","A","Bb","B"];
const DEG       = ["I","II","III","IV","V","VI","VII"];
const MAJ_TYPES = ["maj","min","min","maj","maj","min","dim"];
const MIN_TYPES = ["min","dim","maj","min","min","maj","maj"];
const FN_MAJ    = ["Tonika","Supertonica","Mediante","Subdominante","Dominante","Submediane","Leitton"];
const FN_MIN    = ["Tonika","Supertonica","Mediante","Subdominante","Dominante","Submediane","Subtonica"];

const FC = {
  Tonika:      { bg:"#e8f5e9", bd:"#81c784", tx:"#2e7d32" },
  Subdominante:{ bg:"#e3f2fd", bd:"#64b5f6", tx:"#1565c0" },
  Dominante:   { bg:"#fff8e1", bd:"#ffd54f", tx:"#e65100" },
  Supertonica: { bg:"#f1f8e9", bd:"#aed581", tx:"#558b2f" },
  Mediante:    { bg:"#ede7f6", bd:"#ba68c8", tx:"#6a1b9a" },
  Submediane:  { bg:"#fce4ec", bd:"#f06292", tx:"#880e4f" },
  Leitton:     { bg:"#fbe9e7", bd:"#ff8a65", tx:"#bf360c" },
  Subtonica:   { bg:"#fbe9e7", bd:"#ff8a65", tx:"#bf360c" },
};

// ─── Design Tokens ────────────────────────────────────────────────────────────

const T = {
  bg:           '#FCFCFA',
  surface:      '#FFFFFF',
  surfaceSoft:  '#F6F6F3',
  surfaceAccent:'#F8FBF9',
  border:       '#E8E8E2',
  text:         '#171717',
  textSoft:     '#6B6B66',
  textFaint:    '#A1A19A',
  accent:       '#6FAE8D',
  accentStrong: '#4F8C6B',
  accentSoft:   '#E7F3EC',
  blueSoft:     '#EEF4FA',
  sandSoft:     '#F5F0E8',
  rc:           22,   // radius card
  rp:           999,  // radius pill
  ri:           16,   // radius input
  shadow:       '0 10px 30px rgba(0,0,0,0.04)',
};

const DS = {
  card:    { borderRadius: T.rc, background: T.surface, border: `1px solid ${T.border}`, boxShadow: T.shadow, padding: 16 },
  soft:    { borderRadius: T.rc, background: T.surfaceSoft, border: `1px solid ${T.border}`, padding: 16 },
  accent:  { borderRadius: T.rc, background: T.accentSoft, border: `1px solid ${T.accent}`, padding: 16 },
  pill:    { borderRadius: T.rp, border: 'none', cursor: 'pointer', fontFamily: 'Inter,sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 14px' },
  pillSm:  { borderRadius: T.rp, border: 'none', cursor: 'pointer', fontFamily: 'Inter,sans-serif', fontSize: 11, fontWeight: 600, padding: '4px 10px' },
  inp:     { borderRadius: T.ri, border: `1px solid ${T.border}`, padding: '10px 14px', fontFamily: 'Inter,sans-serif', fontSize: 14, outline: 'none', background: T.surface, width: '100%', boxSizing: 'border-box' },
  label:   { fontSize: 11, fontWeight: 600, color: T.textFaint, letterSpacing: '0.06em', fontFamily: 'Inter,sans-serif', textTransform: 'uppercase' },
  h1:      { fontSize: 26, fontWeight: 600, color: T.text, fontFamily: 'Inter,sans-serif', letterSpacing: '-0.02em', lineHeight: '30px', margin: 0 },
  h2:      { fontSize: 20, fontWeight: 600, color: T.text, fontFamily: 'Inter,sans-serif', letterSpacing: '-0.01em', lineHeight: '24px', margin: 0 },
  body:    { fontSize: 14, color: T.textSoft, fontFamily: 'Inter,sans-serif', lineHeight: '22px' },
  small:   { fontSize: 12, color: T.textSoft, fontFamily: 'Inter,sans-serif', lineHeight: '18px', fontWeight: 500 },
};

const MONTHS = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const WDAYS  = ["Mo","Di","Mi","Do","Fr","Sa","So"];

// ─── Music helpers ────────────────────────────────────────────────────────────

function pickNotes(sharps) { return sharps >= 0 ? NOTES_SHARP : NOTES_FLAT; }
function noteIdx(n) {
  const a = NOTES_SHARP.indexOf(n);
  if (a >= 0) return a;
  return NOTES_FLAT.indexOf(n);
}

function buildScale(root, isMinor, sharps) {
  const notes = pickNotes(sharps);
  let ri = notes.indexOf(root);
  if (ri === -1) {
    const alt = sharps >= 0 ? NOTES_FLAT : NOTES_SHARP;
    ri = alt.indexOf(root);
    if (ri === -1) return [];
    return scaleFrom(ri, isMinor, alt);
  }
  return scaleFrom(ri, isMinor, notes);
}
function scaleFrom(ri, isMinor, notes) {
  const steps = isMinor ? [2,1,2,2,1,2,2] : [2,2,1,2,2,2,1];
  const s = [notes[ri]]; let cur = ri;
  for (const st of steps.slice(0,6)) { cur = (cur + st) % 12; s.push(notes[cur]); }
  return s;
}
function buildChords(scale, isMinor) {
  const types = isMinor ? MIN_TYPES : MAJ_TYPES;
  const fns   = isMinor ? FN_MIN    : FN_MAJ;
  return scale.map((note, i) => ({
    degree: DEG[i], root: note, type: types[i],
    label: note + (types[i]==="maj" ? "" : types[i]==="min" ? "m" : "°"),
    fn: fns[i],
  }));
}
function triad(ri, type, notes) {
  if (type==="maj") return [notes[ri], notes[(ri+4)%12], notes[(ri+7)%12]];
  if (type==="min") return [notes[ri], notes[(ri+3)%12], notes[(ri+7)%12]];
  return [notes[ri], notes[(ri+3)%12], notes[(ri+6)%12]];
}
function chordNotes(root, type, sharps) {
  const notes = pickNotes(sharps);
  let ri = notes.indexOf(root);
  if (ri === -1) {
    const alt = sharps >= 0 ? NOTES_FLAT : NOTES_SHARP;
    ri = alt.indexOf(root);
    if (ri === -1) return [root];
    return triad(ri, type, alt);
  }
  return triad(ri, type, notes);
}
function dateKey(y, m, d) {
  return y + "-" + String(m+1).padStart(2,"0") + "-" + String(d).padStart(2,"0");
}

// ─── Mini Piano (root-position: starts at root, notes always ascending) ───────

// semitone-in-octave → white key index within octave (0-6)
const SEMI_TO_WPOS = [0,0,1,1,2,3,3,4,4,5,5,6];
// semitone-in-octave → is black key?
const SEMI_IS_BLK  = [0,1,0,1,0,0,1,0,1,0,1,0];
// white-key-in-octave → semitone
const WPOS_TO_SEMI = [0,2,4,5,7,9,11];
// which white keys have a black key to their RIGHT (C D F G A)
const WHITE_HAS_BLK_RIGHT = [true,true,false,true,true,true,false];

function semiToAbsWhite(s) {
  return Math.floor(s/12)*7 + SEMI_TO_WPOS[s%12];
}

function MiniPiano({ notes, color }) {
  if (!notes || notes.length === 0) return null;
  const ww=20, wh=52, bw=12, bh=32;

  const rootSemi = noteIdx(notes[0]);
  if (rootSemi < 0) return null;

  // Build ascending semitone positions (root position)
  const semis = [rootSemi];
  let prev = rootSemi;
  for (let i = 1; i < notes.length; i++) {
    let s = noteIdx(notes[i]);
    if (s < 0) continue;
    while (s <= prev) s += 12;
    semis.push(s);
    prev = s;
  }

  const startWhite = semiToAbsWhite(semis[0]);
  const lastWhite  = semiToAbsWhite(semis[semis.length-1]);
  const endWhite   = Math.max(lastWhite + 1, startWhite + 6);
  const numWhite   = endWhite - startWhite + 1;
  const totalW     = numWhite * ww;

  const activeSemis = new Set(semis.map(s => s % 12));

  // Build white keys
  const whiteKeys = [];
  for (let w = startWhite; w <= endWhite; w++) {
    const oct      = Math.floor(w / 7);
    const wInOct   = w % 7;
    const absSemi  = oct * 12 + WPOS_TO_SEMI[wInOct];
    const xPos     = (w - startWhite) * ww;
    whiteKeys.push({ absSemi, semiInOct: absSemi % 12, xPos, wInOct });
  }

  return (
    <svg width={totalW} height={wh} style={{ display:"block", borderRadius:4 }}>
      {whiteKeys.map((k, i) => (
        <rect key={"w"+i} x={k.xPos} y={0} width={ww-1} height={wh} rx={3}
          fill={activeSemis.has(k.semiInOct) ? color : "#fff"}
          stroke="#e0e0e0" strokeWidth={1} />
      ))}
      {whiteKeys.map((k, i) => {
        if (!WHITE_HAS_BLK_RIGHT[k.wInOct]) return null;
        const bSemiInOct = (k.semiInOct + 1) % 12;
        const bx = k.xPos + ww - bw/2;
        if (bx + bw > totalW) return null;
        return (
          <rect key={"b"+i} x={bx} y={0} width={bw} height={bh} rx={2}
            fill={activeSemis.has(bSemiInOct) ? color : "#2a2a2a"} />
        );
      })}
      {whiteKeys.map((k, i) => {
        if (!activeSemis.has(k.semiInOct)) return null;
        const n = notes.find(x => noteIdx(x) % 12 === k.semiInOct) || "";
        return (
          <text key={"wl"+i} x={k.xPos+ww/2-.5} y={wh-5}
            textAnchor="middle" fontSize={7} fontWeight="700"
            fill="#fff" fontFamily="Inter,sans-serif">{n}</text>
        );
      })}
      {whiteKeys.map((k, i) => {
        if (!WHITE_HAS_BLK_RIGHT[k.wInOct]) return null;
        const bSemiInOct = (k.semiInOct + 1) % 12;
        if (!activeSemis.has(bSemiInOct)) return null;
        const bx = k.xPos + ww - bw/2;
        if (bx + bw > totalW) return null;
        const n = notes.find(x => noteIdx(x) % 12 === bSemiInOct) || "";
        return (
          <text key={"bl"+i} x={bx+bw/2} y={bh-4}
            textAnchor="middle" fontSize={6} fontWeight="700"
            fill="#fff" fontFamily="Inter,sans-serif">{n}</text>
        );
      })}
    </svg>
  );
}

// ─── Circle of Fifths ────────────────────────────────────────────────────────

function CircleOfFifths({ selected, onSelect, mode, onToggle }) {
  const r=114, ir=72, cx=150, cy=150;
  const majorC = FC.Tonika;
  const minorC = FC.Subdominante;

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:12 }}>
      <svg width={300} height={300} viewBox="0 0 300 300"
        style={{ touchAction:"manipulation" }}>

        {CIRCLE.map((item, i) => {
          const a  = (i*30 - 90) * Math.PI / 180;
          const x  = cx + r  * Math.cos(a);
          const y  = cy + r  * Math.sin(a);
          const ix = cx + ir * Math.cos(a);
          const iy = cy + ir * Math.sin(a);
          const sel = selected?.key === item.key;
          const outerActive = sel && mode === "major";
          const innerActive = sel && mode === "minor";
          const hitR = 22; // hitbox radius for outer
          const hitRi = 18; // hitbox radius for inner

          return (
            <g key={item.key}>
              {/* ── Outer: Dur ── */}
              <circle cx={x} cy={y} r={20}
                fill={outerActive ? majorC.bg : "#f7f7f7"}
                stroke={outerActive ? majorC.bd : "#e0e0e0"}
                strokeWidth={outerActive ? 2.5 : 1}
                pointerEvents="none" />
              <text x={x} y={y+1}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={11} fontWeight="600"
                fill={outerActive ? majorC.tx : "#555"}
                fontFamily="Inter,sans-serif"
                pointerEvents="none">
                {item.major}
              </text>
              {/* Invisible hitbox – outer, larger tap target */}
              <circle cx={x} cy={y} r={hitR}
                fill="transparent"
                stroke="none"
                style={{ cursor:"pointer" }}
                onClick={() => { onSelect(item); if (mode !== "major") onToggle("major"); }} />

              {/* ── Inner: Moll ── */}
              <circle cx={ix} cy={iy} r={15}
                fill={innerActive ? minorC.bg : "#f0f0f0"}
                stroke={innerActive ? minorC.bd : "#e4e4e4"}
                strokeWidth={innerActive ? 2 : 1}
                pointerEvents="none" />
              <text x={ix} y={iy+1}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={8} fontWeight={innerActive ? "700" : "500"}
                fill={innerActive ? minorC.tx : "#999"}
                fontFamily="Inter,sans-serif"
                pointerEvents="none">
                {item.minor}
              </text>
              {/* Invisible hitbox – inner, larger tap target */}
              <circle cx={ix} cy={iy} r={hitRi}
                fill="transparent"
                stroke="none"
                style={{ cursor:"pointer" }}
                onClick={e => {
                  e.stopPropagation();
                  onSelect(item);
                  if (mode !== "minor") onToggle("minor");
                }} />
            </g>
          );
        })}

        <circle cx={cx} cy={cy} r={36} fill="#f5f5f5" stroke="#e8e8e8" strokeWidth={1} pointerEvents="none" />
        <text x={cx} y={cy-8} textAnchor="middle" fontSize={8} fill="#ccc" fontFamily="Inter,sans-serif" pointerEvents="none">Circle</text>
        <text x={cx} y={cy+8} textAnchor="middle" fontSize={8} fill="#ccc" fontFamily="Inter,sans-serif" pointerEvents="none">of 5ths</text>
      </svg>

      <div style={{ display:"flex", gap:6 }}>
        {["major","minor"].map(m => (
          <button key={m} onClick={() => onToggle(m)} style={{
            padding:"5px 18px", borderRadius:20, border:"none", cursor:"pointer",
            background: mode===m ? "#222" : "#f0f0f0",
            color: mode===m ? "#fff" : "#999",
            fontFamily:"Inter,sans-serif", fontSize:11, fontWeight:600,
          }}>
            {m==="major" ? "DUR" : "MOLL"}
          </button>
        ))}
      </div>
      <div style={{ fontSize:10, color:"#bbb", fontFamily:"Inter,sans-serif", marginTop:-4 }}>
        Außen = Dur · Innen = Moll
      </div>
    </div>
  );
}

// ─── Chord Card ───────────────────────────────────────────────────────────────

function ChordCard({ chord, sharps, expanded, onClick }) {
  const fc = FC[chord.fn] || FC.Tonika;
  const cn = chordNotes(chord.root, chord.type, sharps);
  return (
    <div onClick={onClick} style={{
      borderRadius:10, border:"1px solid " + (expanded ? fc.bd : "#ebebeb"),
      background: expanded ? fc.bg : "#fafafa",
      padding:"10px 12px", cursor:"pointer", transition:"all 0.15s",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <div style={{
          width:28, height:28, borderRadius:7, flexShrink:0,
          display:"flex", alignItems:"center", justifyContent:"center",
          background:fc.bg, border:"1px solid " + fc.bd,
          fontFamily:"Inter,sans-serif", fontSize:10, fontWeight:700, color:fc.tx,
        }}>{chord.degree}</div>
        <div style={{ fontFamily:"Inter,sans-serif", fontSize:14, fontWeight:700, color:"#1a1a1a", width:40, flexShrink:0 }}>{chord.label}</div>
        <div style={{ flex:1, fontSize:10, color:"#bbb", fontFamily:"Inter,sans-serif", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{chord.fn}</div>
        <div style={{ display:"flex", gap:3, flexShrink:0 }}>
          {cn.map((n,i) => (
            <span key={i} style={{
              fontSize:10, fontFamily:"Inter,sans-serif", fontWeight:600,
              color:fc.tx, background:fc.bg, border:"1px solid " + fc.bd,
              borderRadius:4, padding:"1px 5px",
            }}>{n}</span>
          ))}
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid " + fc.bd }}>
          <MiniPiano notes={cn} color={fc.bd} />
          <div style={{ marginTop:8, fontSize:11, color:fc.tx, fontFamily:"Inter,sans-serif", lineHeight:1.6 }}>
            Grundstellung · {chord.type==="maj"?"Dur":chord.type==="min"?"Moll":"Verm."}-Dreiklang · {cn[0]} – {cn[1]} – {cn[2]}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Scale Tab ────────────────────────────────────────────────────────────────

function ScaleTab({ onOpenInGenerator }) {
  const [sel,    setSel]    = useState(CIRCLE[0]);
  const [mode,   setMode]   = useState("major");
  const [exp,    setExp]    = useState(null);
  const [subTab, setSubTab] = useState("skalen"); // "skalen" | "kadenzen" | "modulation"

  const isMinor = mode === "minor";
  const root    = isMinor ? sel.minor.replace("m","") : sel.major;
  const scale   = buildScale(root, isMinor, sel.sharps);
  const chords  = buildChords(scale, isMinor);
  const vz      = sel.sharps;

  function handleSelect(s) { setSel(s); setExp(null); }
  function handleToggle(m) { setMode(m); setExp(null); }

  const vzText = vz === 0 ? "Keine Vorzeichen"
    : vz > 0 ? vz + " Kreuz" + (vz > 1 ? "e" : "")
    : Math.abs(vz) + " B" + (Math.abs(vz) > 1 ? "s" : "");

  const accidentalNotes = scale.filter(n => n.includes("#") || n.includes("b"));

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {/* Header */}
      <div>
        <div style={{ ...DS.label, color:T.textFaint, marginBottom:4 }}>Skalen & Kadenzen</div>
        <div style={{ fontFamily:"'Instrument Serif',Georgia,serif", fontSize:26, color:T.text, fontStyle:"italic" }}>
          {isMinor ? sel.minor : sel.major}
        </div>
        <div style={{ ...DS.small, color:T.textFaint, marginTop:2 }}>
          {vzText}{accidentalNotes.length > 0 && ` · ${accidentalNotes.join(", ")}`} · {isMinor ? "Natürliches Moll" : "Dur-Tonleiter"}
        </div>
      </div>

      {/* Circle of Fifths in card */}
      <div style={{ ...DS.card, padding:"16px 0 8px", display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
        <CircleOfFifths selected={sel} onSelect={handleSelect} mode={mode} onToggle={handleToggle} />
      </div>

      {/* Segmented control */}
      <div style={{ display:"flex", gap:0, borderRadius:T.ri, overflow:"hidden", border:`1px solid ${T.border}`, background:T.surfaceSoft }}>
        {[{id:"skalen",label:"Skalen"},{id:"kadenzen",label:"Kadenzen"},{id:"modulation",label:"Modulation"}].map(t => (
          <button key={t.id} onClick={() => { setSubTab(t.id); setExp(null); }} style={{
            flex:1, padding:"10px 6px", border:"none", cursor:"pointer",
            background: subTab===t.id ? T.text : "transparent",
            color: subTab===t.id ? "#fff" : T.textSoft,
            fontFamily:"Inter,sans-serif", fontSize:12, fontWeight:600,
            borderRight: t.id !== "modulation" ? `1px solid ${T.border}` : "none",
            transition:"all 0.15s",
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Skalen sub-tab ── */}
      {subTab === "skalen" && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {/* Scale notation */}
          <div style={{ ...DS.card, padding:12 }}>
            <div style={{ ...DS.label, color:T.textFaint, marginBottom:8 }}>Notation</div>
            <ScaleNotation scale={scale} sharps={sel.sharps} />
          </div>

          {/* Scale notes */}
          <div style={{ ...DS.card, padding:12 }}>
            <div style={{ ...DS.label, color:T.textFaint, marginBottom:10 }}>Töne der Skala</div>
            <div style={{ display:"flex", gap:5, flexWrap:"wrap", justifyContent:"center" }}>
              {scale.map((note, i) => {
                const hasAcc = note.includes("#") || note.includes("b");
                const base   = hasAcc ? note[0] : note;
                const acc    = hasAcc ? note.slice(1) : "";
                return (
                  <div key={i} style={{
                    width:42, height:50, borderRadius:12,
                    display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
                    background: i===0 ? T.text : T.surfaceSoft,
                    border:`1px solid ${i===0 ? T.text : T.border}`,
                  }}>
                    <div style={{ fontSize:14, fontWeight:700, color:i===0?"#fff":T.text, fontFamily:"Inter,sans-serif", lineHeight:1 }}>
                      {base}{acc && <span style={{ fontSize:9, verticalAlign:"super", color:i===0?"rgba(255,255,255,0.6)":T.accentStrong }}>{acc==="♯"||acc==="#"?"♯":"♭"}</span>}
                    </div>
                    <div style={{ fontSize:8, color:i===0?"rgba(255,255,255,0.5)":T.textFaint, fontFamily:"Inter,sans-serif", marginTop:2, fontWeight:600 }}>{DEG[i]}</div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Chord cards */}
          <div style={{ ...DS.label, color:T.textFaint, marginBottom:2 }}>Stufenakkorde</div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {chords.map((c, i) => (
              <ChordCard key={i} chord={c} sharps={sel.sharps}
                expanded={exp===i} onClick={() => setExp(exp===i ? null : i)} />
            ))}
          </div>
        </div>
      )}

      {/* ── Kadenzen sub-tab ── */}
      {subTab === "kadenzen" && (
        <CadenceSubTab
          sel={sel} mode={mode}
          onOpenInGenerator={onOpenInGenerator}
          onModulate={(newSel, newMode) => { setSel(newSel); setMode(newMode); }}
        />
      )}

      {/* ── Modulation sub-tab ── */}
      {subTab === "modulation" && <ModulationSubTab sel={sel} mode={mode} />}
    </div>
  );
}

// ─── Cadence data ─────────────────────────────────────────────────────────────

const CADENCES = {
  major: [
    { name:"Authentische Kadenz (V–I)", tag:"Klassik · Grundlage", chords:["I","IV","V","I"],
      desc:"Die wichtigste Kadenz der westlichen Musik. Die Dominante (V) erzeugt maximale Spannung durch den Leitton, der einen Halbton unter der Tonika liegt. Das Ohr erwartet die Auflösung auf I geradezu zwingend.",
      usage:"Bach-Choräle, klassische Sonaten, nahezu jeder Schlusstakt." },
    { name:"Plagale Kadenz (IV–I)", tag:"Klassik · Hymnisch", chords:["IV","I"],
      desc:"Auch 'Amen-Kadenz'. Die Subdominante löst direkt in die Tonika auf. Das Ergebnis klingt sanfter, meditativer – ohne die Spannung der Dominante.",
      usage:"Kirchenmusik, Gospel, Schlusstakt nach authentischer Kadenz." },
    { name:"Halbkadenz (I–V)", tag:"Klassik · Offen", chords:["I","V"],
      desc:"Die Musik endet auf der Dominante – das Ohr bleibt in Spannung. Wirkt wie ein Fragezeichen. Häufig am Ende der ersten Phrase einer Periode.",
      usage:"Perioden-Enden, Fragen in Dialog-Strukturen." },
    { name:"Trugschluss (V–VI)", tag:"Klassik · Überraschung", chords:["V","VI"],
      desc:"Die Dominante löst nicht in die Tonika auf, sondern in den VI-Akkord. Das überrascht das Ohr und wird genutzt, um eine Phrase zu verlängern.",
      usage:"Beethoven, Schubert, moderner Pop als 'Keep going'-Effekt." },
    { name:"II–V–I (Jazz)", tag:"Jazz · Unverzichtbar", chords:["II","V","I"],
      desc:"Das Rückgrat des Jazz. Der II-Akkord ersetzt IV als Subdominant-Funktion und führt über V zur Tonika. Im Jazz als Septakkorde: IIm7 – V7 – Imaj7.",
      usage:"Alle Jazz-Standards, Bossa Nova, Soul, R&B." },
    { name:"I–V–VI–IV (Pop)", tag:"Pop · Allgegenwärtig", chords:["I","V","VI","IV"],
      desc:"Die meistgespielte Progression in der Popmusik. Stabilität (I), Aufwärts (V), Emotion (VI-Moll), Wärme (IV). Endlos wiederholbar.",
      usage:"Hunderte von Hits – 'Let It Be', 'No Woman No Cry' u.v.m." },
    { name:"I–IV–V–I (Blues)", tag:"Blues · Rock", chords:["I","IV","V","I"],
      desc:"Die Basis des 12-Takt-Blues. I, IV und V sind die drei Primärfunktionen. Im Blues oft als Dominant-7-Akkorde gespielt (I7, IV7, V7).",
      usage:"Blues, Rock, Country, Folk." },
    { name:"Andalusische Kadenz", tag:"Flamenco · Modal", chords:["I","VII","VI","V"],
      desc:"Eine absteigende Basslinie gibt dieser Kadenz ihren spanischen Klang. Der V-Akkord ist hier Dur (harmonisches Moll / Entlehnung).",
      usage:"Flamenco, Latin, dramatische Film-Scores." },
    { name:"Romantische Kadenz (I–VI–IV–V)", tag:"50s · Pop", chords:["I","VI","IV","V"],
      desc:"Die 'Herzschmerz-Kadenz' der 1950er. Der VI-Moll-Akkord nach der Tonika erzeugt eine melancholische Wendung, bevor IV und V die Spannung aufbauen.",
      usage:"Doo-Wop, 50s Rock'n'Roll, Pop-Balladen." },
    { name:"Kreisquinten-Kadenz", tag:"Jazz · Modulierend", chords:["I","IV","VII","III","VI","II","V","I"],
      desc:"Eine vollständige Reise durch den Quintenzirkel zurück zur Tonika. Jeder Akkord steht eine Quinte unter dem vorherigen.",
      usage:"Jazz-Standards, Chopin-Etüden." },
  ],
  minor: [
    { name:"Authentische Moll-Kadenz", tag:"Klassik · Dramatisch", chords:["I","IV","V","I"],
      desc:"In Moll ist die Dominante oft als Durakkord gespielt (harmonisches Moll), um den Leitton zu erzeugen. Das gibt der Kadenz eine besonders dunkle Intensität.",
      usage:"Bach, Mozart (Moll-Sätze), Filmmusik, Metal." },
    { name:"i–VI–III–VII", tag:"Film · Melancholie", chords:["I","VI","III","VII"],
      desc:"Eine der emotionalsten Progressionen in Moll. Der abfallende Charakter von i über VI nach III erzeugt Wehmut. Der VII-Akkord gibt ihr eine epische, filmische Qualität.",
      usage:"Filmmusik, Progressive Rock, Emotional-Pop." },
    { name:"Phrygische Kadenz", tag:"Modal · Spanisch", chords:["I","IV","I","V"],
      desc:"In Verbindung mit der phrygischen Skala (Halbton nach oben) entsteht ein orientalisch-spanischer Charakter.",
      usage:"Flamenco, Metal, Bach (Toccata und Fuge d-Moll)." },
    { name:"Moll-Andalusisch (i–VII–VI–V)", tag:"Flamenco · Episch", chords:["I","VII","VI","V"],
      desc:"Die absteigende Linie i–VII–VI–V ist eine der kraftvollsten in der Musik. Der Dur-V-Akkord erzeugt maximale Erwartung.",
      usage:"Rodrigo 'Aranjuez', Pink Floyd 'Comfortably Numb'." },
    { name:"Neapolitanische Kadenz", tag:"Klassik · Chromatisch", chords:["IV","I","V","I"],
      desc:"Der Neapolitaner (Durakkord auf der erniedrigten zweiten Stufe, bII) tritt als Subdominant-Ersatz auf. Fremd, dunkel, chromatisch.",
      usage:"Schubert, Beethoven (Mondscheinsonate)." },
    { name:"Epische Moll-Schleife", tag:"Episch · Loop", chords:["I","VI","III","VII"],
      desc:"Als Loop gespielt einer der wirkungsvollsten Untermalungen für Improvisationen. Stabil genug für endlose Wiederholung.",
      usage:"Chopin-Nocturnes, Filmscores, Jazz-Moll-Improvisationen." },
  ],
};

// ─── Modulation Tips (dynamic, based on selected key) ────────────────────────

// Helper: get CIRCLE entry that is N steps clockwise from given index
function circleStep(idx, n) {
  return CIRCLE[(idx + n + 12) % 12];
}

// Helper: find CIRCLE index by key
function circleIdx(sel) {
  return CIRCLE.findIndex(c => c.key === sel.key);
}

// Helper: dominant root of a given major key (5th scale degree = 7 semitones up)
function dominantOf(majorRoot, sharps) {
  const notes = pickNotes(sharps);
  const ri = notes.indexOf(majorRoot);
  if (ri === -1) return majorRoot;
  return notes[(ri + 7) % 12];
}

// Helper: tritone sub = 6 semitones up from given note
function tritoneSub(root, sharps) {
  const notes = pickNotes(sharps);
  const ri = notes.indexOf(root);
  if (ri === -1) {
    // try the other array
    const alt = sharps >= 0 ? NOTES_FLAT : NOTES_SHARP;
    const ri2 = alt.indexOf(root);
    if (ri2 === -1) return root;
    return alt[(ri2 + 6) % 12];
  }
  return notes[(ri + 6) % 12];
}

// Helper: major third up (4 semitones)
function majThirdUp(root, sharps) {
  const notes = pickNotes(sharps);
  const ri = notes.indexOf(root);
  if (ri === -1) { const alt = sharps>=0?NOTES_FLAT:NOTES_SHARP; return alt[(alt.indexOf(root)+4)%12]; }
  return notes[(ri + 4) % 12];
}

// Helper: minor third up (3 semitones)  
function minThirdUp(root, sharps) {
  const notes = pickNotes(sharps);
  const ri = notes.indexOf(root);
  if (ri === -1) { const alt = sharps>=0?NOTES_FLAT:NOTES_SHARP; return alt[(alt.indexOf(root)+3)%12]; }
  return notes[(ri + 3) % 12];
}

// Helper: note 1 semitone up (for half-step modulation)
function halfStepUp(root) {
  const ri = NOTES_FLAT.indexOf(root);
  if (ri >= 0) return NOTES_FLAT[(ri + 1) % 12];
  const ri2 = NOTES_SHARP.indexOf(root);
  return NOTES_SHARP[(ri2 + 1) % 12];
}

// Helper: note 2 semitones up
function wholeStepUp(root) {
  const ri = NOTES_FLAT.indexOf(root);
  if (ri >= 0) return NOTES_FLAT[(ri + 2) % 12];
  const ri2 = NOTES_SHARP.indexOf(root);
  return NOTES_SHARP[(ri2 + 2) % 12];
}

function buildModulationTips(sel, mode) {
  const isMinor  = mode === "minor";
  const tonic    = isMinor ? sel.minor : sel.major;       // e.g. "G" or "Em"
  const tonicRoot = isMinor ? sel.minor.replace("m","") : sel.major; // e.g. "G" or "E"
  const modeLabel = isMinor ? "Moll" : "Dur";
  const idx      = circleIdx(sel);

  // Neighbour keys
  const domKey   = circleStep(idx, 1);   // one step clockwise = Dominanttonart
  const subdKey  = circleStep(idx, -1);  // one step counter = Subdominanttonart
  const relKey   = isMinor ? sel         : sel; // relative is same CIRCLE entry, other mode

  // Dominant of current tonic (V)
  const domChordRoot = dominantOf(tonicRoot, sel.sharps);
  const domV7        = domChordRoot + "7";

  // Subdominant key details
  const subdTonic = isMinor ? subdKey.minor : subdKey.major;
  const subdDomRoot = dominantOf(isMinor ? subdKey.minor.replace("m","") : subdKey.major, subdKey.sharps);

  // Dominant key details
  const domTonic = isMinor ? domKey.minor : domKey.major;
  const domKeyV7root = dominantOf(isMinor ? domKey.minor.replace("m","") : domKey.major, domKey.sharps);

  // Relative mode
  const relTonic = isMinor ? sel.major : sel.minor;
  const relIsMinor = !isMinor;
  const relRoot  = isMinor ? sel.major : sel.minor.replace("m","");
  const relV7    = dominantOf(relRoot, sel.sharps) + "7";

  // Tritone sub of V7
  const triSub = tritoneSub(domChordRoot, sel.sharps) + "7";

  // Terzverwandte (major 3rd up and minor 3rd up from tonic)
  const majThird = majThirdUp(tonicRoot, sel.sharps);
  const minThird = minThirdUp(tonicRoot, sel.sharps);
  const majThirdTonic = majThird + (isMinor ? "m" : "");
  const minThirdTonic = minThird + (isMinor ? "m" : "");

  // Half step / whole step up
  const halfUp = halfStepUp(tonicRoot) + " " + modeLabel;
  const wholeUp = wholeStepUp(tonicRoot) + " " + modeLabel;

  // Quintenzirkel chain: 4 steps from current
  const chain = [sel, circleStep(idx,-1), circleStep(idx,-2), circleStep(idx,-3)];
  const chainLabels = chain.map(c => isMinor ? c.minor : c.major).join(" → ");
  const chainIIVI = chain.map(c => {
    const r = isMinor ? c.minor.replace("m","") : c.major;
    const notes = pickNotes(c.sharps);
    const ri = notes.indexOf(r);
    const ii = ri >= 0 ? notes[(ri+2)%12]+"m7" : "IIm7";
    const v  = ri >= 0 ? notes[(ri+7)%12]+"7"  : "V7";
    return ii + "–" + v + "–" + (isMinor ? c.minor : c.major);
  }).join(" | ");

  // Dorisch and Mixolydisch on same root
  const dorisch   = tonicRoot + "-Dorisch";
  const mixolyd   = tonicRoot + "-Mixolydisch";
  const dorV7char = pickNotes(sel.sharps)[(noteIdx(tonicRoot)+9)%12]; // characteristic: major 6
  const mixV7char = pickNotes(sel.sharps)[(noteIdx(tonicRoot)+10)%12]; // characteristic: b7

  // Scale chords for pivot example: II in current = possible pivot
  const scaleNotes = buildScale(tonicRoot, isMinor, sel.sharps);
  const IIroot = scaleNotes[1];
  const IIlabel = IIroot + (isMinor ? "°" : "m");
  // IIm is also VI in the subdominant key — find its role there
  const subdScale = buildScale(isMinor ? subdKey.minor.replace("m","") : subdKey.major, isMinor, subdKey.sharps);
  const IIinSubd  = subdScale[0] === IIroot ? "I" : subdScale.indexOf(IIroot) >= 0 ? DEG[subdScale.indexOf(IIroot)] : "II";

  return [
    {
      title: "Pivot-Akkord: Der sanfteste Weg",
      tag: "Klassik · Jazz",
      icon: "⇄",
      desc: "Ein Akkord, der in beiden Tonarten existiert, dient als Brücke. Das Ohr hört ihn zuerst in der alten Tonart, re-interpretiert ihn dann in der neuen. Klingt fließend und natürlich – der Zuhörer merkt den Wechsel kaum.",
      example: tonic + " → " + subdTonic + ": Der " + IIlabel + "-Akkord ist II in " + tonic + " und kann als IV in " + subdTonic + " re-interpretiert werden. Spiele: " + tonic + " – " + IIlabel + " (= Pivot) – " + subdDomRoot + "7 – " + subdTonic + ".",
      steps: [
        "Spiele eine Phrase in " + tonic,
        "Erreiche den Pivot-Akkord " + IIlabel + " (funktioniert in beiden Tonarten)",
        "Führe " + subdDomRoot + "7 (V7 von " + subdTonic + ") ein",
        "Löse in " + subdTonic + " auf",
      ],
    },
    {
      title: "Dominante der Zieltonart (V7/neu)",
      tag: "Klassik · Pop",
      icon: "→",
      desc: "Kündige die neue Tonart durch ihren eigenen Dominant-7-Akkord an. Das Ohr hört die neue Tonika sofort, sobald der V7 erklingt – der Tritonus im V7 zeigt eindeutig auf die neue Tonika.",
      example: tonic + " → " + domTonic + ": Spiele " + domKeyV7root + "7 (= V7 von " + domTonic + ") direkt vor " + domTonic + ". Dieser Akkord enthält einen Ton, der in " + tonic + " nicht vorkommt und das Ohr sofort in die neue Richtung zieht.",
      steps: [
        "Beende eine Phrase in " + tonic,
        "Spiele " + domKeyV7root + "7 (Dominant-7 von " + domTonic + ") für 1–2 Takte",
        "Löse direkt in " + domTonic + " auf",
        "Bestätige die neue Tonika mit einer Kadenz",
      ],
    },
    {
      title: "Direkte Modulation (plötzlicher Sprung)",
      tag: "Pop · Rock",
      icon: "↑",
      desc: "Kein Pivot, keine Vorbereitung – einfach in die neue Tonart springen. Wirkt überraschend und energetisch. Besonders beliebt als Steigerungseffekt im letzten Refrain.",
      example: "Du spielst in " + tonic + " und springst direkt zu " + halfUp + " oder " + wholeUp + ". Einfach nach dem letzten " + tonic + "-Akkord die neue Tonika anschlagen – kein Übergang nötig. Hörer erleben einen sofortigen Energieschub.",
      steps: [
        "Beende eine Phrase auf " + tonic + " (oder " + domV7 + ")",
        "Spiele ohne Pause direkt " + halfUp + " oder " + wholeUp,
        "Halte das neue Zentrum mit einer Kadenz fest",
      ],
    },
    {
      title: "Modulation über den Quintenzirkel",
      tag: "Jazz · Improvisation",
      icon: "◎",
      desc: "Navigiere durch mehrere Tonarten im Quintenzirkel. Jeder Schritt ist eine Quarte aufwärts oder Quinte abwärts. Das Ohr folgt diesem Zug natürlich – jede neue Tonart klingt wie eine logische Fortsetzung.",
      example: "Von " + tonic + " aus: " + chainLabels + ". In jeder Tonart ein II–V–I spielen:\n" + chainIIVI + "\nDann weiter durch den Quintenzirkel oder zurück nach " + tonic + ".",
      steps: [
        "Spiele II–V–I in " + tonic,
        "Statt der Tonika: direkt das IIm7 der nächsten Tonart (" + subdTonic + ") anspielen",
        "II–V–I in " + subdTonic + " spielen",
        "So viele Schritte wie gewünscht, dann zurück zu " + tonic,
      ],
    },
    {
      title: "Tritonus-Substitution als Modulationsbrücke",
      tag: "Jazz",
      icon: "↔",
      desc: "Ersetze den V7 deiner aktuellen Tonart durch den Dominant-7-Akkord, der einen Tritonus entfernt liegt. Dieser klingt fremd, leitet aber dennoch sauber in die Tonika. Du kannst so aus einer völlig anderen Richtung ankommen.",
      example: "Du bist in " + tonic + ". Der normale V7 ist " + domV7 + ". Der Tritonus-Ersatz dafür ist " + triSub + ". Spiele " + triSub + " statt " + domV7 + " – du klingst einen Moment nach " + tritoneSub(domChordRoot, sel.sharps) + "-Dur, landest aber trotzdem sauber in " + tonic + ".",
      steps: [
        "Erkenne deinen normalen V7 = " + domV7,
        "Ersetze ihn durch den Tritonus-Sub = " + triSub,
        triSub + " für 1–2 Takte spielen",
        "Auflösung in " + tonic,
      ],
    },
    {
      title: "Relative Modulation (Dur ↔ Moll)",
      tag: "Klassik · Film",
      icon: "↕",
      desc: "Wechsel zwischen " + tonic + " und seiner Paralleltonart " + relTonic + ". Beide teilen dieselben Töne – kein Akkord ist komplett fremd. Ein einziger Akkord kann in beiden Tonarten funktionieren.",
      example: tonic + " → " + relTonic + ": " + relTonic + " ist bereits in " + tonic + " als " + (isMinor ? "III" : "VI") + "-Akkord enthalten. Kündige " + relTonic + " durch seinen Dominantakkord " + relV7 + " an: " + tonic + " – " + relV7 + " – " + relTonic + ".",
      steps: [
        "Spiele eine Phrase in " + tonic,
        "Füge " + relV7 + " (V7 von " + relTonic + ") ein",
        "Löse in " + relTonic + " auf",
        "Bestätige die neue Tonika durch Wiederholung",
      ],
    },
    {
      title: "Modale Farbe (Modus wechseln)",
      tag: "Jazz · Modal",
      icon: "≡",
      desc: "Wechsel den Modus auf demselben Grundton " + tonicRoot + ". Das klingt wie eine Stimmungsänderung, nicht wie ein Tonartwechsel – du bleibst auf " + tonicRoot + ", aber die Farbe des Klangs ändert sich komplett.",
      example: tonicRoot + " als Zentrum: Wechsel zwischen " + dorisch + " (= " + tonicRoot + "-Moll mit erhöhter 6 = " + dorV7char + " als Charakterton) und " + mixolyd + " (= " + tonicRoot + "-Dur mit erniedrigter 7 = " + mixV7char + " als Charakterton). Nur ein oder zwei Töne ändern sich – der Effekt ist enorm.",
      steps: [
        "Bleibe auf Grundton " + tonicRoot,
        "Wechsel zu " + dorisch + ": betone " + dorV7char + " als charakteristischen Ton",
        "Wechsel zu " + mixolyd + ": betone " + mixV7char + " als charakteristischen Ton",
        "Kehre zum Ausgangsmodus zurück oder modifiziere weiter",
      ],
    },
    {
      title: "Chromatische Mediante (Terzverwandte)",
      tag: "Romantik · Film",
      icon: "♯",
      desc: "Moduliere in eine Tonart, die eine Terz entfernt liegt. Klingt magisch, unerwartet, filmisch. Beethoven und Schubert liebten diesen Effekt – er erzeugt eine plötzliche harmonische 'Farbänderung'.",
      example: tonic + " → " + majThirdTonic + " oder " + minThirdTonic + ": Spiele " + majThird + " " + modeLabel + " direkt nach " + tonic + ". Gemeinsamer Ton ist " + majThird + " (als Terz in " + tonic + " und als Grundton in " + majThirdTonic + "). Der Effekt klingt überraschend und emotional aufgeladen.",
      steps: [
        "Beende eine Phrase auf " + tonic,
        "Springe direkt zu " + majThirdTonic + " (große Terz) oder " + minThirdTonic + " (kleine Terz)",
        "Den gemeinsamen Ton " + majThird + " als melodische Brücke nutzen",
        "Neue Tonika mit Kadenz bestätigen",
      ],
    },
  ];
}

// ─── Cadence Sub-Tab ─────────────────────────────────────────────────────────

function CadenceSubTab({ sel, mode, onOpenInGenerator, onModulate }) {
  const [active, setActive] = useState(null);
  const [view,   setView]   = useState("list"); // "list" | "network"

  const isMinor = mode === "minor";
  const root    = isMinor ? sel.minor.replace("m","") : sel.major;
  const scale   = buildScale(root, isMinor, sel.sharps);
  const chords  = buildChords(scale, isMinor);
  const cads    = CADENCES[mode];

  function resolve(deg) { const i = DEG.indexOf(deg); return i >= 0 ? chords[i] : null; }

  function handleOpenInGenerator(e, cad) {
    e.stopPropagation();
    const resolved = cad.chords.map(deg => resolve(deg)).filter(Boolean);
    onOpenInGenerator({ sel, mode, chords: resolved });
  }

  return (
    <div>
      {/* View toggle */}
      <div style={{ display:"flex", gap:0, borderRadius:10, overflow:"hidden", border:"1px solid #e0e0e0", marginBottom:16 }}>
        {[{id:"list",label:"Kadenzen"},{id:"network",label:"Akkord-Netz"}].map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={{
            flex:1, padding:"9px 8px", border:"none", cursor:"pointer",
            background: view===v.id ? "#1a1a1a" : "#fafafa",
            color: view===v.id ? "#fff" : "#888",
            fontFamily:"Inter,sans-serif", fontSize:12, fontWeight:600,
            borderRight: v.id==="list" ? "1px solid #e0e0e0" : "none",
            transition:"all 0.15s",
          }}>{v.label}</button>
        ))}
      </div>

      {/* Akkord-Netz */}
      {view === "network" && (
        <ChordNetwork sel={sel} mode={mode} onModulate={onModulate} />
      )}

      {/* Kadenzen list */}
      {view === "list" && (
        <div>
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:22, fontWeight:700, fontFamily:"Inter,sans-serif", color:"#111", letterSpacing:"-0.02em" }}>
              Kadenzen in {isMinor ? sel.minor : sel.major}
            </div>
            <div style={{ fontSize:11, color:"#bbb", fontFamily:"Inter,sans-serif", marginTop:2 }}>
              {cads.length} Progressionen · tippen für Details
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {cads.map((cad, i) => {
              const isOpen = active === i;
              return (
                <div key={i} onClick={() => setActive(isOpen ? null : i)} style={{
                  borderRadius:10, border:"1px solid " + (isOpen ? "#d0d0d0" : "#ebebeb"),
                  background: isOpen ? "#f9f9f9" : "#fafafa",
                  padding:"12px 14px", cursor:"pointer", transition:"all 0.15s",
                }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
                    <div>
                      <div style={{ fontFamily:"Inter,sans-serif", fontSize:12, fontWeight:600, color:"#333" }}>{cad.name}</div>
                      <div style={{ fontFamily:"Inter,sans-serif", fontSize:10, color:"#aaa", marginTop:2 }}>{cad.tag}</div>
                    </div>
                    <div style={{ fontSize:16, color:"#ccc", flexShrink:0 }}>{isOpen ? "−" : "+"}</div>
                  </div>
                  <div style={{ display:"flex", gap:4, marginTop:10, flexWrap:"wrap", alignItems:"center" }}>
                    {cad.chords.map((deg, j) => {
                      const c = resolve(deg);
                      if (!c) return null;
                      const fc = FC[c.fn];
                      return (
                        <div key={j} style={{ display:"flex", alignItems:"center", gap:4 }}>
                          {j > 0 && <span style={{ fontSize:11, color:"#ccc" }}>→</span>}
                          <div style={{
                            padding:"4px 9px", borderRadius:7,
                            background:fc.bg, border:"1px solid " + fc.bd,
                            fontFamily:"Inter,sans-serif", fontSize:12, fontWeight:700, color:fc.tx,
                          }}>{c.label}</div>
                        </div>
                      );
                    })}
                  </div>
                  {isOpen && (
                    <div>
                      <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #e8e8e8", fontSize:12, color:"#555", fontFamily:"Inter,sans-serif", lineHeight:1.8 }}>
                        {cad.desc}
                      </div>
                      <div style={{ marginTop:4, fontSize:11, color:"#aaa", fontFamily:"Inter,sans-serif", fontStyle:"italic" }}>
                        Verwendung: {cad.usage}
                      </div>
                      {onOpenInGenerator && (
                        <button onClick={(e) => handleOpenInGenerator(e, cad)} style={{
                          marginTop:12, padding:"7px 14px", borderRadius:8, border:"none",
                          background:"#1a1a1a", color:"#fff",
                          fontFamily:"Inter,sans-serif", fontSize:11, fontWeight:600,
                          cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6,
                        }}>
                          Im Generator öffnen →
                        </button>
                      )}
                      <div style={{ marginTop:14, display:"flex", flexDirection:"column", gap:10 }}>
                        {cad.chords.map((deg, j) => {
                          const c = resolve(deg);
                          if (!c) return null;
                          const fc = FC[c.fn];
                          const cn = chordNotes(c.root, c.type, sel.sharps);
                          return (
                            <div key={j} style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                              <div style={{ fontFamily:"Inter,sans-serif", fontSize:12, fontWeight:700, color:fc.tx, width:34, flexShrink:0 }}>{c.label}</div>
                              <div style={{ display:"flex", gap:3, marginRight:6 }}>
                                {cn.map((n,k) => (
                                  <span key={k} style={{
                                    fontSize:10, fontFamily:"Inter,sans-serif", fontWeight:600,
                                    color:fc.tx, background:fc.bg, border:"1px solid " + fc.bd,
                                    borderRadius:4, padding:"1px 5px",
                                  }}>{n}</span>
                                ))}
                              </div>
                              <MiniPiano notes={cn} color={fc.bd} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Modulation Sub-Tab ───────────────────────────────────────────────────────

function ModulationSubTab({ sel, mode }) {
  const [active, setActive] = useState(null);

  const isMinor = mode === "minor";
  const modTips = buildModulationTips(sel, mode);

  return (
    <div>
      <div style={{ marginBottom:14 }}>
        <div style={{ ...DS.h2, fontFamily:"'Instrument Serif',Georgia,serif", fontStyle:"italic" }}>
          Modulation & Tonart-Wechsel
        </div>
        <div style={{ ...DS.small, color:T.textFaint, marginTop:2 }}>
          {modTips.length} Techniken · Beispiele für {isMinor ? sel.minor : sel.major}
        </div>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {modTips.map((tip, i) => {
          const isOpen = active === i;
          return (
            <div key={i} onClick={() => setActive(isOpen ? null : i)} style={{
              ...DS.card, padding:"12px 14px", cursor:"pointer",
              border:`1px solid ${isOpen ? T.accent : T.border}`,
              background: isOpen ? T.accentSoft : T.surface,
              transition:"all 0.15s",
            }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
                <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                  <div style={{
                    width:32, height:32, borderRadius:8, flexShrink:0,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    background:T.surfaceSoft, fontSize:15,
                  }}>{tip.icon}</div>
                  <div>
                    <div style={{ fontFamily:"Inter,sans-serif", fontSize:13, fontWeight:600, color:T.text }}>{tip.title}</div>
                    <div style={{ ...DS.small, color:T.textFaint, marginTop:2 }}>{tip.tag}</div>
                  </div>
                </div>
                <div style={{ fontSize:16, color:T.textFaint, flexShrink:0, marginTop:2 }}>{isOpen ? "−" : "+"}</div>
              </div>

              {isOpen && (
                <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${T.border}` }}>
                  <div style={{ ...DS.body, marginBottom:12 }}>{tip.desc}</div>

                  <div style={{
                    borderRadius:T.ri, background:T.surface, border:`1px solid ${T.border}`,
                    padding:"10px 12px", marginBottom:12,
                  }}>
                    <div style={{ ...DS.label, color:T.textFaint, marginBottom:5 }}>Beispiel</div>
                    <div style={{ ...DS.small }}>{tip.example}</div>
                  </div>

                  <div style={{ ...DS.label, color:T.textFaint, marginBottom:8 }}>Schritte</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                    {tip.steps.map((step, si) => (
                      <div key={si} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                        <div style={{
                          width:20, height:20, borderRadius:"50%", flexShrink:0,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          background:T.text,
                          fontFamily:"Inter,sans-serif", fontSize:10, fontWeight:700, color:"#fff",
                        }}>{si+1}</div>
                        <div style={{ ...DS.body, paddingTop:1 }}>{step}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── Repertoire View ─────────────────────────────────────────────────────────

function RepertoireView({ pieces, setPieces }) {
  const [search,      setSearch]      = useState("");
  const [selPiece,    setSelPiece]    = useState(null);
  const [sessions,    setSessions]    = useState([]);
  const [sessLoading, setSessLoading] = useState(false);
  const [filterFrom,  setFilterFrom]  = useState("");
  const [filterTo,    setFilterTo]    = useState("");
  const [showForm,    setShowForm]    = useState(false);
  const [editPiece,   setEditPiece]   = useState(null);
  const [formTitle,   setFormTitle]   = useState("");
  const [formUrl,     setFormUrl]     = useState("");
  const [formDesc,    setFormDesc]    = useState("");
  const [editSessId,      setEditSessId]      = useState(null);
  const [editSessNote,    setEditSessNote]    = useState("");
  const [editSessMeasure, setEditSessMeasure] = useState("");

  const filtered = pieces.filter(p => p.title.toLowerCase().includes(search.toLowerCase()));

  function openDetail(p) {
    setSelPiece(p); setSessLoading(true); setSessions([]);
    getPieceSessions(p.id)
      .then(setSessions)
      .catch(() => setSessions([]))
      .finally(() => setSessLoading(false));
  }

  function openEdit(p) {
    setEditPiece(p); setFormTitle(p.title); setFormUrl(p.url||""); setFormDesc(p.description||"");
    setShowForm(true); setSelPiece(null);
  }

  function openNew() {
    setEditPiece(null); setFormTitle(""); setFormUrl(""); setFormDesc("");
    setShowForm(true);
  }

  async function submitForm() {
    if (!formTitle.trim()) return;
    if (editPiece) {
      const updated = await updatePiece(editPiece.id, { title: formTitle.trim(), url: formUrl.trim()||null, description: formDesc.trim()||null });
      setPieces(prev => prev.map(p => p.id === editPiece.id ? { ...p, ...updated } : p));
    } else {
      const created = await createPiece({ title: formTitle.trim(), url: formUrl.trim()||null, description: formDesc.trim()||null });
      setPieces(prev => [...prev, { ...created, session_count:0, last_practiced:null }].sort((a,b)=>a.title.localeCompare(b.title)));
    }
    setShowForm(false); setEditPiece(null);
  }

  async function handleDelete(p) {
    if (!window.confirm(`"${p.title}" wirklich löschen?`)) return;
    await deletePiece(p.id).catch(()=>{});
    setPieces(prev => prev.filter(x => x.id !== p.id));
    if (selPiece?.id === p.id) setSelPiece(null);
  }

  async function handleDeleteSession(id) {
    await deleteSession(id).catch(()=>{});
    setSessions(prev => prev.filter(s => s.id !== id));
    setPieces(prev => prev.map(p => p.id === selPiece?.id
      ? { ...p, session_count: Math.max(0, (p.session_count||1) - 1) }
      : p
    ));
  }

  async function handleSaveSessionEdit(id) {
    const updated = await updateSession(id, { notes: editSessNote.trim() || null, measure_range: editSessMeasure.trim() || null }).catch(()=>null);
    if (updated) setSessions(prev => prev.map(s => s.id === id ? { ...s, notes: updated.notes, measure_range: updated.measure_range } : s));
    setEditSessId(null);
  }

  const filteredSessions = sessions.filter(s => {
    if (filterFrom && s.date < filterFrom) return false;
    if (filterTo   && s.date > filterTo)   return false;
    return true;
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Stück suchen…"
          style={{ ...DS.inp, flex:1 }}/>
        <button onClick={openNew} style={{ ...DS.pill, background:T.text, color:"#fff", flexShrink:0 }}>+ Neu</button>
      </div>

      {/* New/Edit form */}
      {showForm && (
        <div style={{ ...DS.soft, marginBottom:14, display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ ...DS.label, color:T.textSoft }}>{editPiece ? "Stück bearbeiten" : "Neues Stück"}</div>
          <input value={formTitle} onChange={e=>setFormTitle(e.target.value)} placeholder="Titel *" style={DS.inp}/>
          <input value={formUrl} onChange={e=>setFormUrl(e.target.value)} placeholder="Link (YouTube, MuseScore, …)" style={DS.inp}/>
          <textarea value={formDesc} onChange={e=>setFormDesc(e.target.value)} placeholder="Notizen" rows={2} style={{ ...DS.inp, resize:"vertical" }}/>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={submitForm} disabled={!formTitle.trim()} style={{ ...DS.pill, flex:1, padding:"8px", background:T.text, color:"#fff", opacity:formTitle.trim()?1:0.4 }}>Speichern</button>
            <button onClick={()=>setShowForm(false)} style={{ ...DS.pill, background:T.surface, color:T.textSoft, border:`1px solid ${T.border}` }}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Piece list */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:"32px 0", ...DS.small, color:T.textFaint }}>
          {pieces.length === 0 ? "Noch keine Stücke. + Neu klicken." : "Keine Treffer."}
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {filtered.map(p => {
            const isOpen = selPiece?.id === p.id;
            return (
              <div key={p.id} style={{ ...DS.card, padding:0, overflow:"hidden", border:`1px solid ${isOpen ? T.accent : T.border}`, transition:"all 0.12s" }}>
                {/* Header row */}
                <div onClick={() => isOpen ? setSelPiece(null) : openDetail(p)}
                  style={{ padding:"12px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:10, background: isOpen ? T.accentSoft : T.surface }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:"Inter,sans-serif", fontSize:13, fontWeight:600, color:T.text }}>{p.title}</div>
                    <div style={{ display:"flex", gap:10, marginTop:2 }}>
                      <span style={{ ...DS.small, color:T.textFaint }}>
                        {p.session_count} {p.session_count === 1 ? "Session" : "Sessions"}
                      </span>
                      {p.last_practiced && (
                        <span style={{ ...DS.small, color:T.textFaint }}>zuletzt {p.last_practiced}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                    <button onClick={e=>{ e.stopPropagation(); openEdit(p); }} style={{ ...DS.pillSm, background:T.surface, color:T.textSoft, border:`1px solid ${T.border}` }}>✎</button>
                    <button onClick={e=>{ e.stopPropagation(); handleDelete(p); }} style={{ ...DS.pillSm, background:"#fff8f8", color:"#e88", border:"1px solid #fdd" }}>×</button>
                  </div>
                  <div style={{ fontSize:14, color:T.textFaint, marginLeft:4 }}>{isOpen ? "−" : "+"}</div>
                </div>

                {isOpen && (
                  <div style={{ padding:"0 14px 14px", borderTop:`1px solid ${T.border}` }}>
                    {p.url && (
                      <a href={p.url} target="_blank" rel="noreferrer" style={{ fontFamily:"Inter,sans-serif", fontSize:12, color:T.accentStrong, display:"block", marginTop:10, wordBreak:"break-all" }}>{p.url}</a>
                    )}
                    {p.description && (
                      <div style={{ ...DS.body, marginTop:6, whiteSpace:"pre-wrap" }}>{p.description}</div>
                    )}

                    <div style={{ marginTop:12 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, flexWrap:"wrap" }}>
                        <div style={{ ...DS.label, color:T.textFaint }}>Übungshistorie</div>
                        <input type="date" value={filterFrom} onChange={e=>setFilterFrom(e.target.value)}
                          style={{ padding:"4px 8px", borderRadius:8, border:`1px solid ${T.border}`, fontFamily:"Inter,sans-serif", fontSize:11, outline:"none", color:T.text }}/>
                        <span style={{ ...DS.small, color:T.textFaint }}>–</span>
                        <input type="date" value={filterTo} onChange={e=>setFilterTo(e.target.value)}
                          style={{ padding:"4px 8px", borderRadius:8, border:`1px solid ${T.border}`, fontFamily:"Inter,sans-serif", fontSize:11, outline:"none", color:T.text }}/>
                        {(filterFrom||filterTo) && <button onClick={()=>{setFilterFrom("");setFilterTo("");}} style={{ ...DS.pillSm, background:T.surfaceSoft, color:T.textSoft, border:`1px solid ${T.border}` }}>×</button>}
                      </div>
                      {sessLoading ? (
                        <div style={{ ...DS.small, color:T.textFaint }}>Lade…</div>
                      ) : filteredSessions.length === 0 ? (
                        <div style={{ ...DS.small, color:T.textFaint }}>{sessions.length === 0 ? "Noch keine Sessions." : "Keine Sessions im Zeitraum."}</div>
                      ) : (
                        <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                          {filteredSessions.map(s => (
                            <div key={s.id} style={{ padding:"9px 11px", borderRadius:T.ri, background:T.surface, border:`1px solid ${T.border}` }}>
                              {editSessId === s.id ? (
                                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                                  <div style={{ ...DS.label, color:T.textFaint }}>{s.date}</div>
                                  <input value={editSessMeasure} onChange={e=>setEditSessMeasure(e.target.value)}
                                    placeholder="Takte (z. B. 1–16)" style={{ ...DS.inp, fontSize:12 }}/>
                                  <textarea value={editSessNote} onChange={e=>setEditSessNote(e.target.value)} rows={3}
                                    placeholder="Notizen" style={{ ...DS.inp, fontSize:12, resize:"vertical" }}/>
                                  <div style={{ display:"flex", gap:5 }}>
                                    <button onClick={()=>handleSaveSessionEdit(s.id)} style={{ ...DS.pillSm, background:T.text, color:"#fff" }}>Speichern</button>
                                    <button onClick={()=>setEditSessId(null)} style={{ ...DS.pillSm, background:T.surface, color:T.textSoft, border:`1px solid ${T.border}` }}>Abbrechen</button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
                                  <div style={{ ...DS.small, color:T.textFaint, flexShrink:0, width:80 }}>{s.date}</div>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    {s.measure_range && (
                                      <div style={{ display:"inline-flex", alignItems:"center", gap:4, marginBottom:3, padding:"1px 7px", borderRadius:T.rp, background:T.accentSoft, border:`1px solid ${T.accent}` }}>
                                        <span style={{ fontSize:10, fontWeight:700, color:T.accentStrong, fontFamily:"Inter,sans-serif" }}>Takte</span>
                                        <span style={{ fontSize:11, color:T.accentStrong, fontFamily:"Inter,sans-serif" }}>{s.measure_range}</span>
                                      </div>
                                    )}
                                    <div style={{ ...DS.small, color:T.textSoft, whiteSpace:"pre-wrap" }}>{s.notes || <span style={{color:T.textFaint}}>–</span>}</div>
                                  </div>
                                  <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                                    <button onClick={()=>{ setEditSessId(s.id); setEditSessNote(s.notes||""); setEditSessMeasure(s.measure_range||""); }}
                                      style={{ ...DS.pillSm, background:T.surface, color:T.textSoft, border:`1px solid ${T.border}` }}>✎</button>
                                    <button onClick={()=>handleDeleteSession(s.id)}
                                      style={{ ...DS.pillSm, background:"#fff8f8", color:"#e88", border:"1px solid #fdd" }}>×</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Progress Tab ─────────────────────────────────────────────────────────────

function DayDetail({ dk, entries, pieces, onAdd, onRemove, onSessionAdded, onSessionRemoved }) {
  const [pick,          setPick]          = useState("");
  const [sessions,      setSessions]      = useState([]);
  const [sessLoading,   setSessLoading]   = useState(true);
  const [selPieceId,    setSelPieceId]    = useState("");
  const [sessionNote,   setSessionNote]   = useState("");
  const [measureRange,  setMeasureRange]  = useState("");
  const [newTitle,      setNewTitle]      = useState("");
  const [newUrl,        setNewUrl]        = useState("");
  const [newMode,       setNewMode]       = useState(false);

  const raw = entries[dk];
  const de = raw ? (Array.isArray(raw) ? raw : raw.scales) : [];

  const parts = dk.split("-");
  const label = parseInt(parts[2]) + ". " + MONTHS[parseInt(parts[1])-1] + " " + parts[0];

  useEffect(() => {
    setSessLoading(true);
    getSessionsForDay(dk)
      .then(setSessions)
      .catch(() => setSessions([]))
      .finally(() => setSessLoading(false));
  }, [dk]);

  async function handleAddSession() {
    if (!selPieceId && !newTitle.trim()) return;
    try {
      let pid = selPieceId ? parseInt(selPieceId) : null;
      let newPiece = null;
      if (newMode && newTitle.trim()) {
        newPiece = await createPiece({ title: newTitle.trim(), url: newUrl.trim() || null });
        pid = newPiece.id;
        setNewTitle(""); setNewUrl(""); setNewMode(false);
      }
      if (!pid) return;
      const s = await createSession({ piece_id: pid, date: dk, notes: sessionNote.trim() || null, measure_range: measureRange.trim() || null });
      setSessions(prev => [...prev.filter(x => x.piece_id !== s.piece_id), s]);
      setSelPieceId(""); setSessionNote(""); setMeasureRange("");
      onSessionAdded(newPiece, dk, pid);
    } catch {}
  }

  async function handleRemoveSession(id, pieceId) {
    await deleteSession(id).catch(() => {});
    setSessions(prev => prev.filter(s => s.id !== id));
    onSessionRemoved(pieceId);
  }

  const availablePieces = pieces.filter(p => !sessions.some(s => s.piece_id === p.id));

  return (
    <div style={{ ...DS.card, marginTop:14 }}>
      <div style={{ fontFamily:"'Instrument Serif',Georgia,serif", fontSize:16, fontStyle:"italic", color:T.text, marginBottom:14 }}>{label}</div>

      {/* ── Tonleitern ── */}
      <div style={{ ...DS.label, color:T.textFaint, marginBottom:8 }}>Tonleitern</div>
      {de.length > 0 && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
          {de.map((s, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:T.rp, background:T.accentSoft, border:`1px solid ${T.accent}` }}>
              <span style={{ fontFamily:"Inter,sans-serif", fontSize:11, color:T.accentStrong, fontWeight:600 }}>{s}</span>
              <span onClick={e => { e.stopPropagation(); onRemove(dk, s); }} style={{ fontSize:14, color:T.accent, cursor:"pointer", lineHeight:1 }}>×</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display:"flex", gap:6, marginBottom:18 }}>
        <select value={pick} onChange={e => setPick(e.target.value)} style={{ ...DS.inp, flex:1, color: pick ? T.text : T.textFaint }}>
          <option value="">Tonleiter wählen…</option>
          {CIRCLE.map(c => (
            <optgroup key={c.key} label={c.major}>
              <option value={c.major + " Dur"}>{c.major} Dur</option>
              <option value={c.minor + " Moll"}>{c.minor} Moll</option>
              <option value={c.major + " Dur + " + c.minor + " Moll"}>{c.major} Dur + {c.minor} Moll</option>
              <option value={c.major + " Pentatonik"}>{c.major} Pentatonik</option>
              <option value={c.minor + " Blues"}>{c.minor} Blues</option>
            </optgroup>
          ))}
        </select>
        <button onClick={() => { if (pick) { onAdd(dk, pick); setPick(""); } }} style={{ ...DS.pill, background:T.text, color:"#fff" }}>＋</button>
      </div>

      {/* ── Stücke ── */}
      <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:14 }}>
        <div style={{ ...DS.label, color:T.textFaint, marginBottom:10 }}>Stücke heute</div>

        {sessLoading ? (
          <div style={{ ...DS.small, color:T.textFaint, marginBottom:10 }}>Lade…</div>
        ) : sessions.length > 0 ? (
          <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:12 }}>
            {sessions.map(s => (
              <div key={s.id} style={{ borderRadius:T.ri, border:`1px solid ${T.border}`, background:T.surface, padding:"10px 12px", display:"flex", alignItems:"flex-start", gap:8 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontFamily:"Inter,sans-serif", fontSize:13, fontWeight:600, color:T.text }}>{s.title}</div>
                  {s.measure_range && (
                    <div style={{ display:"inline-flex", alignItems:"center", gap:4, marginTop:3, padding:"2px 8px", borderRadius:T.rp, background:T.accentSoft, border:`1px solid ${T.accent}` }}>
                      <span style={{ fontSize:10, fontWeight:700, color:T.accentStrong, fontFamily:"Inter,sans-serif" }}>Takte</span>
                      <span style={{ fontSize:11, color:T.accentStrong, fontFamily:"Inter,sans-serif" }}>{s.measure_range}</span>
                    </div>
                  )}
                  {s.url && <a href={s.url} target="_blank" rel="noreferrer" style={{ fontFamily:"Inter,sans-serif", fontSize:11, color:T.accentStrong, wordBreak:"break-all", display:"block", marginTop:2 }}>{s.url}</a>}
                  {s.notes && <div style={{ ...DS.small, color:T.textSoft, marginTop:4, whiteSpace:"pre-wrap" }}>{s.notes}</div>}
                </div>
                <span onClick={() => handleRemoveSession(s.id, s.piece_id)} style={{ fontSize:16, color:T.textFaint, cursor:"pointer", lineHeight:1, flexShrink:0 }}>×</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ ...DS.small, color:T.textFaint, marginBottom:10 }}>Noch kein Stück eingetragen.</div>
        )}

        {/* Add session */}
        <div style={{ display:"flex", flexDirection:"column", gap:8, padding:12, borderRadius:T.ri, border:`1px solid ${T.border}`, background:T.surfaceSoft }}>
          <select value={newMode ? "__new__" : selPieceId} onChange={e => { if (e.target.value === "__new__") { setNewMode(true); setSelPieceId(""); } else { setNewMode(false); setSelPieceId(e.target.value); }}}
            style={{ ...DS.inp, color: (selPieceId || newMode) ? T.text : T.textFaint }}>
            <option value="">Stück wählen…</option>
            {availablePieces.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            <option value="__new__">+ Neues Stück erstellen</option>
          </select>
          {newMode && (
            <>
              <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Titel *" style={DS.inp}/>
              <input value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="Link (optional)" style={DS.inp}/>
            </>
          )}
          <input value={measureRange} onChange={e => setMeasureRange(e.target.value)} placeholder="Takte (z. B. 1–16 oder 32–48)" style={DS.inp}/>
          <textarea value={sessionNote} onChange={e => setSessionNote(e.target.value)} placeholder="Notizen zur Session (optional)" rows={2} style={{ ...DS.inp, resize:"vertical" }}/>
          <button onClick={handleAddSession} disabled={!selPieceId && !newTitle.trim()} style={{ ...DS.pill, background:T.text, color:"#fff", opacity: (!selPieceId && !newTitle.trim()) ? 0.4 : 1 }}>Hinzufügen</button>
        </div>
      </div>
    </div>
  );
}

function ProgressTab() {
  const today = new Date();
  const [entries,   setEntries]   = useState({});
  const [pieces,    setPieces]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [syncing,   setSyncing]   = useState(false);
  const [vy, setVy] = useState(today.getFullYear());
  const [vm, setVm] = useState(today.getMonth());
  const [selDay,    setSelDay]    = useState(null);
  const [mainTab,   setMainTab]   = useState("calendar");

  // Load from API on mount
  useEffect(() => {
    Promise.all([getProgress(), getPieces(), getSessionDates()])
      .then(([data, pList, sDates]) => {
        const mapped = {};
        for (const [date, val] of Object.entries(data)) {
          mapped[date] = { scales: val.scales || [], pieces: val.pieces || [] };
        }
        // Merge session dates so calendar dots appear even for session-only days
        for (const date of sDates) {
          if (!mapped[date]) mapped[date] = { scales: [], pieces: [], _hasSessions: true };
          else mapped[date]._hasSessions = true;
        }
        setEntries(mapped);
        setPieces(pList);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function persistDay(dk, scales, pieces) {
    setSyncing(true);
    try {
      if (scales.length === 0 && pieces.length === 0) await deleteDay(dk);
      else await saveDay(dk, { scales, pieces });
    } catch {}
    finally { setSyncing(false); }
  }

  function getDay(prev, dk) {
    const d = prev[dk];
    if (!d) return { scales: [], pieces: [] };
    if (Array.isArray(d)) return { scales: d, pieces: [] }; // backwards compat
    return d;
  }

  function addScale(dk, scale) {
    setEntries(prev => {
      const day = getDay(prev, dk);
      if (day.scales.includes(scale)) return prev;
      const next = { ...day, scales: [...day.scales, scale] };
      persistDay(dk, next.scales, next.pieces);
      return { ...prev, [dk]: next };
    });
  }
  function removeScale(dk, scale) {
    setEntries(prev => {
      const day = getDay(prev, dk);
      const next = { ...day, scales: day.scales.filter(s => s !== scale) };
      persistDay(dk, next.scales, next.pieces);
      const updated = { ...prev, [dk]: next };
      if (next.scales.length === 0 && next.pieces.length === 0) delete updated[dk];
      return updated;
    });
  }
  // Called after a session was added (optionally with a newly created piece)
  function handleSessionAdded(newPiece, sessionDate, pieceId) {
    // If a new piece was created, add it to the list
    if (newPiece?.id) {
      setPieces(prev => [...prev.filter(p => p.id !== newPiece.id), { ...newPiece, session_count: 1, last_practiced: sessionDate }].sort((a,b) => a.title.localeCompare(b.title)));
    } else if (pieceId && sessionDate) {
      // Update session_count and last_practiced for existing piece
      setPieces(prev => prev.map(p => p.id === pieceId
        ? { ...p, session_count: (p.session_count||0) + 1, last_practiced: p.last_practiced > sessionDate ? p.last_practiced : sessionDate }
        : p
      ));
    }
    // Mark the day as having activity (so calendar dot appears)
    setEntries(prev => {
      const day = getDay(prev, selDay);
      return { ...prev, [selDay]: { ...day, _hasSessions: true } };
    });
  }
  function handleSessionRemoved(pieceId) {
    // Decrement session_count for the piece
    setPieces(prev => prev.map(p => p.id === pieceId
      ? { ...p, session_count: Math.max(0, (p.session_count||1) - 1) }
      : p
    ));
  }
  function prevMonth() {
    if (vm === 0) { setVm(11); setVy(y => y-1); } else setVm(m => m-1);
    setSelDay(null);
  }
  function nextMonth() {
    if (vm === 11) { setVm(0); setVy(y => y+1); } else setVm(m => m+1);
    setSelDay(null);
  }

  const daysInMonth = new Date(vy, vm+1, 0).getDate();
  const firstDow    = (new Date(vy, vm, 1).getDay() + 6) % 7;
  const todayKey    = dateKey(today.getFullYear(), today.getMonth(), today.getDate());

  const cells = [];
  for (let i=0; i<firstDow; i++) cells.push(null);
  for (let d=1; d<=daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthPfx     = vy + "-" + String(vm+1).padStart(2,"0");
  const practicedDays = Object.entries(entries).filter(([k, v]) => {
    if (!k.startsWith(monthPfx)) return false;
    const scales = Array.isArray(v) ? v : v.scales || [];
    const pieces = v?.pieces || [];
    return scales.length > 0 || pieces.length > 0 || v?._hasSessions;
  }).length;

  return (
    <div>
      {loading && (
        <div style={{ textAlign:"center", padding:"40px 0", ...DS.small, color:T.textFaint }}>Lade…</div>
      )}
      {!loading && <>
        <div style={{ marginBottom:16 }}>
          <div style={{ ...DS.label, color:T.textFaint, marginBottom:4 }}>Journal</div>
          <div style={{ fontFamily:"'Instrument Serif',Georgia,serif", fontSize:26, color:T.text, fontStyle:"italic" }}>
            Fortschritt {syncing && <span style={{ fontSize:11, color:T.textFaint, fontStyle:"normal", fontFamily:"Inter,sans-serif", fontWeight:400 }}>· speichern…</span>}
          </div>
          <div style={{ ...DS.small, color:T.textFaint, marginTop:2 }}>
            {practicedDays} Übungstage diesen Monat · {pieces.length} Stücke im Repertoire
          </div>
        </div>

        {/* Main tabs */}
        <div style={{ display:"flex", gap:0, borderRadius:T.ri, overflow:"hidden", border:`1px solid ${T.border}`, background:T.surfaceSoft, marginBottom:20 }}>
          {[{id:"calendar",label:"Kalender"},{id:"repertoire",label:`Repertoire (${pieces.length})`}].map(t => (
            <button key={t.id} onClick={() => setMainTab(t.id)} style={{
              flex:1, padding:"10px 6px", border:"none", cursor:"pointer",
              background: mainTab===t.id ? T.text : "transparent",
              color: mainTab===t.id ? "#fff" : T.textSoft,
              fontFamily:"Inter,sans-serif", fontSize:12, fontWeight:600,
              borderRight: t.id==="calendar" ? `1px solid ${T.border}` : "none",
              transition:"all 0.15s",
            }}>{t.label}</button>
          ))}
        </div>

        {/* ── Calendar tab ── */}
        {mainTab === "calendar" && <>
          <div style={{ ...DS.card, padding:"12px 14px", marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <button onClick={prevMonth} style={{ width:32, height:32, borderRadius:T.ri, border:`1px solid ${T.border}`, background:T.surfaceSoft, cursor:"pointer", fontSize:16, color:T.textSoft }}>‹</button>
              <div style={{ fontFamily:"Inter,sans-serif", fontSize:14, fontWeight:600, color:T.text }}>{MONTHS[vm]} {vy}</div>
              <button onClick={nextMonth} style={{ width:32, height:32, borderRadius:T.ri, border:`1px solid ${T.border}`, background:T.surfaceSoft, cursor:"pointer", fontSize:16, color:T.textSoft }}>›</button>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:4 }}>
              {WDAYS.map(d => (
                <div key={d} style={{ textAlign:"center", ...DS.label, color:T.textFaint, padding:"3px 0" }}>{d}</div>
              ))}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
              {cells.map((day, i) => {
                if (!day) return <div key={i} />;
                const dk   = dateKey(vy, vm, day);
                const de   = entries[dk] ? (Array.isArray(entries[dk]) ? entries[dk] : entries[dk].scales) : [];
                const hasPrac = de.length > 0 || entries[dk]?._hasSessions;
                const isToday = dk === todayKey;
                const isSel   = selDay === dk;
                return (
                  <div key={i} onClick={() => setSelDay(isSel ? null : dk)} style={{
                    borderRadius:10, padding:"5px 3px", cursor:"pointer",
                    textAlign:"center", minHeight:42,
                    background: isSel ? T.accentSoft : isToday ? T.surfaceSoft : "transparent",
                    border:`1px solid ${isSel ? T.accent : isToday ? T.border : "transparent"}`,
                    transition:"all 0.12s",
                  }}>
                    <div style={{ fontFamily:"Inter,sans-serif", fontSize:12, fontWeight: isToday ? 700 : 400, color: isSel ? T.accentStrong : isToday ? T.text : T.textSoft }}>{day}</div>
                    {hasPrac && (
                      <div style={{ display:"flex", justifyContent:"center", gap:2, marginTop:3 }}>
                        <div style={{ width:5, height:5, borderRadius:"50%", background:T.accent }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {selDay && (
            <DayDetail dk={selDay} entries={entries} pieces={pieces}
              onAdd={addScale} onRemove={removeScale}
              onSessionAdded={handleSessionAdded} onSessionRemoved={handleSessionRemoved} />
          )}

          <div style={{ marginTop:8 }}>
            <div style={{ ...DS.label, color:T.textFaint, marginBottom:10 }}>Jahresübersicht {vy}</div>
            <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
              {MONTHS.map((mn, mi) => {
                const dim  = new Date(vy, mi+1, 0).getDate();
                const pfx  = vy + "-" + String(mi+1).padStart(2,"0");
                const prac = Object.entries(entries).filter(([k, v]) => {
                  if (!k.startsWith(pfx)) return false;
                  const sc = Array.isArray(v) ? v : v.scales || [];
                  return sc.length > 0 || v?.pieces?.length > 0 || v?._hasSessions;
                }).length;
                const pct  = prac / dim;
                const isCur = mi === vm;
                return (
                  <div key={mi} onClick={() => { setVm(mi); setSelDay(null); }} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3, cursor:"pointer" }}>
                    <div style={{
                      width:20, height:20, borderRadius:4,
                      background: pct===0 ? T.surfaceSoft : `rgba(111,174,141,${0.2 + pct*0.8})`,
                      border:`1px solid ${isCur ? T.accent : T.border}`,
                    }} />
                    <div style={{ fontSize:7, color:T.textFaint, fontFamily:"Inter,sans-serif" }}>{mn.slice(0,3)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </>}

        {/* ── Repertoire tab ── */}
        {mainTab === "repertoire" && (
          <RepertoireView pieces={pieces} setPieces={setPieces} />
        )}
      </>}
    </div>
  );
}


// ─── Theory Data ──────────────────────────────────────────────────────────────

const THEORY = [
  { chapter:"Grundlagen", icon:"🎵", articles:[
    { title:"Töne und Frequenzen", content:"Ein Ton entsteht durch periodische Schwingungen. Die Frequenz bestimmt die Tonhöhe: A4 schwingt bei 440 Hz, jede Oktave verdoppelt die Frequenz (A5 = 880 Hz). Das westliche System teilt die Oktave in 12 gleichmäßige Halbtöne (gleichschwebende Temperatur).\n\nHALBTON UND GANZTON:\nEin Halbton ist der kleinste Schritt (C → C#). Zwei Halbtöne = ein Ganzton (C → D).\n\nOKTAVIDENTITÄT:\nNoten im Abstand einer Oktave klingen gleich, nur höher/tiefer.\n\nENHARMONIK:\nC# = Db, F# = Gb – klingt identisch auf dem modernen Klavier." },
    { title:"Dur-Tonleiter", content:"Schema: 2-2-1-2-2-2-1 (W-W-H-W-W-W-H).\n\nAlle 12 Durtonarten folgen diesem Muster. C-Dur: keine Vorzeichen.\n\nWARUM KLINGT DUR HELL?\nDie große Terz (4 Ht) zwischen Stufe I und III. Das Ohr assoziiert sie mit Helligkeit und Stabilität.\n\nKREUZE: G(1#) D(2#) A(3#) E(4#) B(5#) F#(6#)\nBS: F(1b) Bb(2b) Eb(3b) Ab(4b) Db(5b) Gb(6b)" },
    { title:"Moll-Tonleitern", content:"NATÜRLICHES MOLL: 2-1-2-2-1-2-2. Kleine Terz zwischen I und III.\n\nHARMONISCHES MOLL: + erhöhte 7. Stufe (Leitton). Schema: 2-1-2-2-1-3-1.\n\nMELODISCHES MOLL: Aufwärts Stufen 6+7 erhöht, abwärts natürlich.\n\nJAZZ-MOLL: Nur die aufsteigende Form des melodischen Molls." },
    { title:"Kirchentonarten (Modi)", content:"7 Modi der Durtonleiter:\n\nIONISCH (I): = Dur. Hell, stabil.\nDORISCH (II): Moll + erhöhte 6. Jazzig, warm. Typisch: Santana, Miles Davis.\nPHRYGISCH (III): Moll + erniedrigte 2. Spanisch, dunkel. Flamenco.\nLYDISCH (IV): Dur + erhöhte 4. Traumhaft, schwebend. Film-Scores.\nMIXOLYDISCH (V): Dur + erniedrigte 7. Blues, Rock. Grateful Dead.\nÄOLISCH (VI): = Natürliches Moll. Melancholisch.\nLOKRISCH (VII): Instabil, selten. Nur in Metal verwendet." },
    { title:"Pentatonik und Blues", content:"DUR-PENTATONIK: Stufen 1-2-3-5-6. Keine Halbtöne → überall verwendbar.\n\nMOLL-PENTATONIK: 1-b3-4-5-b7. Fundament von Blues und Rock.\n\nBLUES-SKALA: Moll-Pentatonik + b5 (Blue Note). Erzeugt Spannung.\n\nWARUM PENTATONIK ÜBERALL KLINGT:\nKeine Halbtöne eliminiert die meisten Dissonanzen. Deshalb der perfekte Einstieg in die Improvisation." },
    { title:"Exotische Skalen", content:"HARMONISCHES MOLL-MODUS (Phrygisch Dominant):\nStufe 5 des harmonischen Molls. Schema: 1-b2-3-4-5-b6-b7. Klingt arabisch, spanisch, mystisch.\n\nGANZTONSKALA:\nNur Ganztöne: C-D-E-F#-G#-Bb. Völlig symmetrisch. Klingt schwebend, ohne tonales Zentrum. Debussy.\n\nVERMINDERTE SKALA (oktatonisch):\nAbwechselnd Halb- und Ganztöne. 8 Töne. Sehr symmetrisch. Jazz, Filmmusik.\n\nCHROMATISCHE SKALA:\nAlle 12 Halbtöne. Kein tonales Zentrum. Als Durchgangsskala in Jazz und Klassik." },
  ]},
  { chapter:"Intervalle", icon:"↕", articles:[
    { title:"Intervall-Übersicht", content:"0 Ht: Prime – identisch\n1 Ht: Kleine Sekunde – sehr dissonant, Reibung\n2 Ht: Große Sekunde – leichte Spannung\n3 Ht: Kleine Terz – Moll-Charakter, sehnsüchtig\n4 Ht: Große Terz – Dur-Charakter, warm\n5 Ht: Reine Quarte – neutral, stabil\n6 Ht: Tritonus – maximale Spannung\n7 Ht: Reine Quinte – stabil, kraftvoll\n8 Ht: Kleine Sexte – melancholisch\n9 Ht: Große Sexte – warm, offen\n10 Ht: Kleine Septime – drängend\n11 Ht: Große Septime – schwebend, jazzig\n12 Ht: Oktave – identisch" },
    { title:"Der Tritonus", content:"6 Halbtöne – die Hälfte der Oktave. Mittelalter: 'Diabolus in Musica'.\n\nIm V7-Akkord bilden Terz und Septime einen Tritonus. In G7: H und F. Dieser Zug zur Auflösung macht V7 so mächtig.\n\nAUFLÖSUNG NACH INNEN: B→C, F→E (zur Terz)\nAUFLÖSUNG NACH AUSSEN: B→A, F→G (zur Sexte)\n\nTRITONUS-SUBSTITUTION: G7 ↔ Db7. Beide teilen denselben Tritonus." },
    { title:"Konsonanz und Dissonanz", content:"KONSONANT: Prime, Oktave, Quinte, Quarte, Terzen, Sexten.\nDISSONANT: Sekunden, Septimen, Tritonus.\n\nDiese Kategorien sind historisch und kulturell bedingt – nicht absolut. Im Jazz sind Septimen 'normal'.\n\nUMKEHRUNGEN: Terz (4Ht) ↔ Sexte (8Ht), Sekunde ↔ Septime, Quarte ↔ Quinte. Summe = 12." },
    { title:"Intervalle hören lernen", content:"ESELSBRÜCKEN:\nKleine Sekunde: Jaws-Thema\nGroße Sekunde: Happy Birthday (erste 2 Noten)\nKleine Terz: Smoke on the Water\nGroße Terz: Oh When the Saints\nReine Quarte: Here Comes the Bride\nTritonus: The Simpsons Theme\nReine Quinte: Star Wars Theme\nKleine Sexte: The Entertainer\nGroße Sexte: My Bonnie Lies Over the Ocean\nKleine Septime: Somewhere (aus West Side Story)\nGroße Septime: Take On Me (Intro)\nOktave: Somewhere Over the Rainbow" },
  ]},
  { chapter:"Akkorde", icon:"🎹", articles:[
    { title:"Dreiklänge", content:"DUR (maj): 4+3 Ht → C-E-G. Hell, stabil.\nMOLL (min): 3+4 Ht → C-Eb-G. Dunkel, emotional.\nVERMINDERT (dim): 3+3 Ht → C-Eb-Gb. Instabil, spannend.\nÜBERMÄSSIG (aug): 4+4 Ht → C-E-G#. Schwebend, unentschlossen.\n\nUMKEHRUNGEN:\nGrundstellung 1-3-5: stabilste Form.\nErste Umkehrung 3-5-1: schwebender.\nZweite Umkehrung 5-1-3: instabilste." },
    { title:"Septakkorde", content:"MAJ7 (1-3-5-7): warm, ruhend → Cmaj7: C-E-G-B\nDOM7 (1-3-5-b7): Spannung, drängend → C7: C-E-G-Bb\nMIN7 (1-b3-5-b7): sanft, jazzig → Cm7: C-Eb-G-Bb\nHALBVERM. (1-b3-b5-b7): dunkel → Cm7b5\nDIM7 (1-b3-b5-bb7): maximal spannend → Cdim7\n\nDOM7 = wichtigster Akkord. Tritonus in Terz+Septime erzeugt stärksten Zug." },
    { title:"Erweiterte Akkorde: 9, 11, 13", content:"NONE (9): Sekunde eine Oktave höher.\nCmaj9: C-E-G-B-D\n\nUNDEZIME (11): Quarte höher.\nCm11: C-Eb-G-Bb-D-F\n\nTREDEZIME (13): Sexte höher.\nC13: C-E-G-Bb-D-F-A\n\nJAZZ-PRAXIS: Nur charakteristische Töne spielen.\nTerz + Septime + Extension – Grundton und Quinte weglassen.\nG13 rootless: B-F-E (Terz+b7+13)" },
    { title:"Sus, Add, Power Chords", content:"SUS4: Quarte statt Terz (C-F-G). Schwebend, unentschlossen.\nSUS2: Sekunde statt Terz (C-D-G). Offen, modern.\n\nADD9: Dreiklang + None ohne Septime → Cadd9: C-E-G-D\nADD11: Dreiklang + Undezime → Cadd11: C-E-G-F\n\nPOWER CHORD: 1+5 (keine Terz). Neutral, kraftvoll. Rock/Metal." },
    { title:"Akkord-Symbole lesen", content:"C = C-Dur Dreiklang\nCm = C-Moll\nC7 = C-Dominant-7\nCmaj7 = C mit großer Septime\nCm7 = C-Moll-7\nCdim = C-vermindert\nCdim7 = C-vollvermindert\nCaug = C-übermäßig\nCsus4 = C-sus4\nC/E = C-Dur, E im Bass (Umkehrung)\nC9 = C7 + None\nC13 = C7 + Tredezime\nCm7b5 = Cm7 mit erniedrigter Quinte (Halbvermindert)" },
  ]},
  { chapter:"Harmonielehre", icon:"🔗", articles:[
    { title:"Funktionsharmonik", content:"TONIKA (T): Ruhe, Heimat → I, III, VI\nSUBDOMINANTE (S): Bewegung → II, IV\nDOMINANTE (D): Spannung → V, VII\n\nGRUNDPRINZIP: T – S – D – T\n\nFUNKTIONSERSATZ:\nI ↔ VI (beide Tonika)\nIV ↔ II (beide Subdominante)\nV ↔ VII (beide Dominante)" },
    { title:"Stimmführung", content:"Akkordtöne so verbinden, dass jede Stimme minimal bewegt.\n\nREGELN:\n1. Gemeinsame Töne halten\n2. Stufenweise Bewegung bevorzugen\n3. Große Sprünge vermeiden\n4. Parallele Quinten/Oktaven meiden (Klassik)\n\nPRAXIS: I–IV–V–I mit geringstmöglichen Fingerbewegungen. Umkehrungen nutzen." },
    { title:"Modulationen", content:"PIVOT-AKKORD: Ein Akkord in beiden Tonarten als Brücke.\nDIREKTE MODULATION: Plötzlicher Sprung (Pop: Halbton rauf im letzten Refrain).\nDOMINANT-MODULATION: V7 der Zieltonart ankündigen.\nTERZVERWANDTE: Modulation um eine Terz. Filmisch, magisch (Beethoven).\n\nFAUSTREGEL: Nachbartonarten im Quintenzirkel = leichteste Ziele." },
    { title:"Modal Interchange", content:"Akkorde aus der gleichnamigen Moll-Tonart in Dur borgen:\n\nbVI = Ab in C-Dur → episch, filmisch\nbVII = Bb in C-Dur → rockig, geerdet\niv = Fm in C-Dur → emotional, melancholisch\n\nBEKANNTE BEISPIELE:\nI–IV–iv–I: Moll-iv gibt Bruch\nI–bVI–bVII–I: typische Rockbewegung" },
    { title:"Sekundärdominanten", content:"Jeden Akkord als temporäre Tonika behandeln:\n\nV7/II = A7 → Dm in C-Dur\nV7/IV = C7 → F in C-Dur\nV7/V = D7 → G (Doppeldominante)\nV7/VI = E7 → Am\n\nWIRKUNG: Der Ziel-Akkord bekommt mehr Gewicht. Häufig in Blues und Pop.\n\nBEISPIEL: I – V7/IV – IV – V7 – I" },
    { title:"Neapolitaner und Leittonwechselklänge", content:"NEAPOLITANER (bII):\nDur-Akkord auf der erniedrigten 2. Stufe. In C-Dur: Db-Dur.\nFunktion: Subdominant-Ersatz. Klingt fremd, dramatisch.\nBEISPIEL: Beethoven Mondscheinsonate.\n\nLEITTONWECHSELKLÄNGE:\nTonika-Leittonwechsel: I ↔ bIII (C-Dur ↔ Eb-Dur)\nSubdominante-Leittonwechsel: IV ↔ bVI (F-Dur ↔ Ab-Dur)\nDominante-Leittonwechsel: V ↔ bVII (G-Dur ↔ Bb-Dur)" },
  ]},
  { chapter:"Quintenzirkel", icon:"◎", articles:[
    { title:"Aufbau und Logik", content:"Uhrzeigersinn: Quinte höher (7 Ht) → C-G-D-A-E-B-F#\nGegen Uhrzeigersinn: Quarte höher (5 Ht) → C-F-Bb-Eb-Ab-Db\n\nNachbarn teilen 6 von 7 Tönen.\nInnenkreis: Parallele Molltonarten (gleiche Vorzeichen).\n\nSTÄRKSTE BEWEGUNG: Gegen Uhrzeigersinn (V→I).\nG→C, D→G, A→D = Dominante zur Tonika." },
    { title:"Vorzeichen merken", content:"KREUZE: G(1) D(2) A(3) E(4) B(5) F#(6) C#(7)\nMerkhilfe: Geh Du Alter Esel Bitte F-Dur Cis\nLetztes Kreuz = Leitton.\n\nBS: F(1) Bb(2) Eb(3) Ab(4) Db(5) Gb(6) Cb(7)\nMerkhilfe: Fettes Brot Essen Alle Deutschen Gerne\nVorletztes b = Grundton der Tonart." },
    { title:"Verwandtschaftsgrade", content:"1 Schritt: 6 gemeinsame Töne. Einfachste Modulation.\n2 Schritte: 5 Töne gemeinsam.\n3 Schritte (Terzverwandte): 4 Töne. Beethoven.\nGegenüber (6 Schritte): Tritonus-Verwandtschaft. Jazz.\n\nParallele Tonart: gleiche Vorzeichen, andere Qualität.\nGleichnamige Tonart: gleicher Grundton, andere Vorzeichen (Modal Interchange)." },
    { title:"Quintenzirkel in der Improvisation", content:"ZIRKEL FOLGEN:\nNach II–V–I weitergehen zur nächsten Tonart (Quarte hoch).\n'Autumn Leaves': E–A–D–G–C–F konsequent durch den Quintenzirkel.\n\nJEDE BEWEGUNG V→I = ein Schritt gegen Uhrzeigersinn.\n\nTRITONUS-SUBSTITUTION:\nGegenüberliegende Tonart im Zirkel. G7 ↔ Db7. Beide lösen nach C auf.\nBasslinie fällt chromatisch: Db→C statt G→C." },
  ]},
  { chapter:"Jazz", icon:"♩", articles:[
    { title:"II–V–I meistern", content:"IN C-DUR: Dm7 – G7 – Cmaj7\nIN c-MOLL: Dm7b5 – G7alt – Cm(maj7)\n\nSKALEN:\nÜber IIm7: Dorisch\nÜber V7: Mixolydisch oder Alteriert\nÜber Imaj7: Ionisch oder Lydisch\n\nWICHTIGSTE ÜBUNG: In allen 12 Tonarten täglich. Quintenzirkel nutzen." },
    { title:"Alterierter Dominantakkord", content:"G7alt = G7 mit alterierten Erweiterungen:\nb9 (Ab), #9 (A#), b13 (Eb)\n\nG7#9 = 'Jimi Hendrix Chord': G-B-D-F-A#\n\nALTERIERTE SKALA: Melodisches Moll ab Stufe 7.\nAuf G7alt: Ab melodisches Moll.\nAlle Noten außer Terz (B) und b7 (F) sind alteriert.\n\nKLANG: Extrem spannungsgeladen, drängt stark zur Tonika." },
    { title:"Jazz-Voicings", content:"SHELL VOICINGS: Grundton + Terz + Septime.\nCmaj7: C–E–B. G7: G–B–F.\n\nROOTLESS VOICINGS: Nur Terz + Septime + Extension.\nG7(9): B–F–A. Bass übernimmt Grundton.\n\nLINKE HAND IM JAZZ:\nVoicings in linker Hand, rechte Hand improvisiert.\n\nDROP-2 VOICINGS:\nZweitoberstem Ton eine Oktave runter. Luftiger Klang." },
    { title:"Reharmonisierung", content:"TRITONUS-SUB: G7 → Db7. Basslinie chromatisch.\n\nSEKUNDÄRDOMINANTEN einfügen: vor wichtige Akkorde deren V7.\n\nMODAL INTERCHANGE: Dur-Akkorde durch Moll borgen.\n\nACCORD-CHAIN: Mehrere II–V–I aneinanderreihen.\n\nBACKCYCLING: Von Zielakkord rückwärts durch Quintenzirkel." },
    { title:"Modale Improvisation", content:"AKKORD–MODUS:\nCmaj7 → Ionisch oder Lydisch\nDm7 → Dorisch\nG7 → Mixolydisch\nG7alt → Alteriert (Melodisch Moll ab VII)\nAm7 → Äolisch oder Dorisch\n\nMODALES DENKEN: Statt 'Töne über Akkord' → 'Ich bin in X-Modus'.\n\nMiles Davis 'So What': D-Dorisch / Eb-Dorisch. Maximal einfach, maximal expressiv." },
    { title:"Blues-Harmonik", content:"12-TAKT-BLUES IN C:\nTakte 1-4: C7 (I7)\nTakte 5-6: F7 (IV7)\nTakte 7-8: C7\nTakt 9: G7 (V7)\nTakt 10: F7\nTakte 11-12: C7 + Turnaround\n\nTURNAROUND: I7–VI7–II7–V7 oder chromatisch.\n\nBLUES-PHRASEN: Call-and-Response zwischen linker und rechter Hand.\n\nBLUES-SKALA über alle drei Akkorde spielen – die Blue Note (b5) erzeugt Spannung." },
    { title:"Chord Melody", content:"Melodie und Akkord gleichzeitig spielen.\n\nTECHNIK:\n1. Melodieton als oberste Stimme\n2. Passenden Akkord darunter\n3. Voicing so wählen, dass Melodieton prominent bleibt\n\nBEISPIEL: Melodieton E über G7 → G7 mit E als Tredezime (G13)\n\nSTIMMFÜHRUNG: Akkorde folgen der Melodie chromatisch.\n\nBEKANNTE BEISPIELE: Joe Pass 'Virtuoso', Bill Evans' Balladen-Arrangements." },
  ]},
  { chapter:"Rhythmus", icon:"𝅘𝅥𝅮", articles:[
    { title:"Taktarten und Metrum", content:"TAKTARTEN:\n4/4: Standardtakt. 4 Viertel pro Takt. Pop, Rock, Jazz.\n3/4: Walzer. 3 Viertel pro Takt. Klassik, Folk.\n6/8: Compound-Takt. 6 Achtel = 2 Gruppen à 3. Ballade-Feeling.\n5/4: Fünfer-Takt. 'Take Five' (Dave Brubeck). Ungewohnt, interessant.\n7/8: Siebener. Prog-Rock, Balkan.\n\nMETRUM: Pulsschlag unter der Musik. Tempo = BPM (Beats per Minute)." },
    { title:"Rhythmische Patterns am Klavier", content:"ALBERTI-BASS (Klassik): Grundton–Quinte–Terz–Quinte. Czerny, Mozart.\n\nSTRIDE PIANO (Jazz): Bass-Note (1,3) und Akkord (2,4) wechselnd. Fats Waller.\n\nBOSSA NOVA: Synkopierter Bass gegen Akkord. Joao Gilberto.\nPattern (4/4): Bass auf 1, Akkord auf 2+ und 3+\n\nFUNK: Betonte Backbeats (2 und 4), synkopierte Akkorde.\n\nGOSPEL: Voller Klang, viele Inversionen, häufige IV-Akkord-Verwendung." },
    { title:"Synkope und Off-Beat", content:"SYNKOPE: Betonung auf einem normalerweise unbetonten Zählzeit.\n\nOFF-BEAT: Zwischen den Hauptzählzeiten (+). Erzeugt Schwung.\n\nJAZZ-SWING: Achtel werden 'swingend' gespielt (langer+kurzer Wert).\n\nPOLYRHYTHMIK: Zwei verschiedene Rhythmen gleichzeitig.\n3 gegen 2 (Hemiole): sehr häufig in Chopin und Latin-Musik.\n\nSUBDIVISION:\nGerader Rhythmus (16tel): Pop, Rock, elektronisch.\nTernärer Rhythmus (Triolen): Jazz, Blues, Gospel." },
    { title:"Übungen für rhythmische Präzision", content:"METRONOM-TRAINING:\n1. Langsam beginnen (60 BPM), erst dann beschleunigen\n2. Metronom auf Beats 2 und 4 (Backbeat) stellen\n3. Metronom auf off-beats (+) stellen\n\nHAND-UNABHÄNGIGKEIT:\nLinke Hand: gleichmäßige Viertel oder Oktaven\nRechte Hand: Melodie/Improvisation\nÜben: Hanon, Cortot, Bach-Inventionen\n\nGROOVE-TRAINING:\nMit Drumloops spielen. Lockerer, flüssiger als Metronom. Fühle den Puls." },
  ]},
  { chapter:"Komposition", icon:"✍", articles:[
    { title:"Melodie schreiben", content:"GRUNDPRINZIPIEN:\n• Bogen: Melodie steigt und fällt (kein Auf-und-Ab ohne Richtung)\n• Wiederholung: Ein Motiv 2-3x wiederholen mit Variation\n• Höhepunkt: Einmal den höchsten Ton als emotionalen Gipfel\n• Stufenweise mit Sprüngen auflösen\n\nMOTIV-ENTWICKLUNG:\nUrform → Wiederholung → Sequenz (höher/tiefer) → Umkehrung → Augmentation (langsamer)\n\nGOLDENE REGEL: Einfachheit gewinnt. Die Ohrwurm-Melodien sind simpel." },
    { title:"Songstruktur", content:"KLASSISCHE POP-STRUKTUR:\nVerse (A) → Chorus (B) → Verse (A) → Chorus (B) → Bridge (C) → Chorus (B)\n\nJAZZ-FORM:\nAABA: 32 Takte. A=8, A=8, B=8 (Bridge), A=8. Rhythm Changes, Many Standards.\nAABC: AABA-Variation. Blues Form: 12 Takte (AAB).\n\nKLASSISCHE FORM:\nSonatenhauptsatzform: Exposition – Durchführung – Reprise\nThema und Variationen: Thema → mehrere Abwandlungen\nRondo: ABACADA..." },
    { title:"Harmonische Spannungsbögen", content:"JEDE PHRASE BRAUCHT:\n• Ausgangspunkt (Tonika)\n• Bewegung weg (Subdominante)\n• Spannung (Dominante)\n• Auflösung (Tonika)\n\nWIE LANGE SPANNUNG HALTEN:\nKurze Phrasen (2-4 Takte): schnelle Auflösung\nLange Phrasen (8-16 Takte): mehrere Spannungsmomente\n\nDYNAMIK DER SPANNUNG:\nLeise + dichte Harmonik → Spannung\nLaut + einfache Harmonik → Energie\nLeise + einfache Harmonik → Entspannung" },
    { title:"Arrangieren für Klavier", content:"REGISTER NUTZEN:\nTiefes Register: Fundament, Autorität, Tiefe\nMittleres Register: Melodie, Harmonie, Ausdruck\nHohes Register: Helligkeit, Brillanz, Details\n\nDICHTE UND TRANSPARENZ:\nViele Töne gleichzeitig = dicht, füllig\nWenige Töne = transparent, klar\n\nKONTRAST:\nLaut/Leise, Hoch/Tief, Dicht/Dünn, Schnell/Langsam\nKontrast erzeugt Interesse und hält die Aufmerksamkeit.\n\nPEDAL-EINSATZ:\nDämpferpedal: Töne verbinden, Resonanz\nSparsam einsetzen – zu viel Pedal = Matsch" },
  ]},
  { chapter:"Improvisation", icon:"🎸", articles:[
    { title:"Erste Schritte", content:"SCHRITT 1: Pentatonik über Am–F–C–G.\nA-Moll-Pentatonik: A-C-D-E-G. Klingt von Anfang an gut.\n\nSCHRITT 2: Rhythmus > Töne.\nEin 2-Noten-Motiv rhythmisch interessant spielen > 20 Noten in 2 Sekunden.\n\nSCHRITT 3: Pausen nutzen.\nStille ist Musik. Das Ohr braucht Atemraum.\n\nSCHRITT 4: Frage-Antwort (2+2 Takte).\nSCHRITT 5: Ein Motiv entwickeln.\nHöher, tiefer, rückwärts, langsamer, schneller." },
    { title:"Skalenwahl über Akkorde", content:"Dmaj7 → D-Ionisch oder D-Lydisch (#4 für Magie)\nEm7 → E-Dorisch (warmer als Äolisch)\nF#m7 → F#-Phrygisch (dunkel) oder Dorisch\nA7 → A-Mixolydisch oder A-Alteriert\nBm7 → B-Dorisch\n\nFAUST­REGEL:\nDur-Akkorde → Ionisch oder Lydisch\nMoll-Akkorde → Dorisch\nDom7-Akkorde → Mixolydisch\nDom7alt-Akkorde → Alteriert" },
    { title:"Licks und Patterns", content:"EIN LICK = eine kurze musikalische Phrase (2-4 Töne meist).\n\nLERNSTRATEGIE:\n1. Lick lernen in C\n2. Sofort in alle 12 Tonarten transponieren\n3. In verschiedenen Rhythmen üben\n4. Kombinieren und variieren\n\nWICHTIGE LICK-TYPEN:\nAnlauf: Skala zum Zielton\nArpeggio: Akkordtöne gebrochen\nBending (simuliert): Triller oder Portamento\nSequenz: Dasselbe Muster auf verschiedenen Stufen\n\nZIEL: Licks werden zum Vokabular – du 'sprichst' statt 'buchstabierst'." },
    { title:"Outside Playing", content:"BEWUSSTES VERLASSEN DER TONART für kurze Zeit.\n\nMETHODE 1: Halbton drüber oder drunter spielen, dann zurück.\nMETHODE 2: Tritonus-Tonart für 2-4 Töne, dann Auflösung.\nMETHODE 3: Chromatische Läufe durch die Akkordtöne.\n\nDER TRICK: Je stärker die Auflösung zurück in die Tonart, desto mehr Outside kannst du dir erlauben.\n\nBEKANNT VON: John Coltrane, Woody Shaw, moderne Jazz-Pianisten.\n\nFAUSTREGEL: Outside → Inside. Nie outside enden ohne Auflösung." },
  ]},
];

// ─── Theory Tab ───────────────────────────────────────────────────────────────

function TheoryTab() {
  const [ch,  setCh]  = useState(0);
  const [art, setArt] = useState(null);

  function selectChapter(i) { setCh(i); setArt(null); }

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <div style={{ ...DS.label, color:T.textFaint, marginBottom:4 }}>Theorie</div>
        <div style={{ fontFamily:"'Instrument Serif',Georgia,serif", fontSize:26, color:T.text, fontStyle:"italic" }}>
          Theorie-Kompendium
        </div>
        <div style={{ ...DS.small, color:T.textFaint, marginTop:2 }}>
          {THEORY.reduce((s,c) => s + c.articles.length, 0)} Artikel in {THEORY.length} Kapiteln
        </div>
      </div>
      <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:16 }}>
        {THEORY.map((c, i) => (
          <button key={i} onClick={() => selectChapter(i)} style={{
            ...DS.pill,
            background: ch===i ? T.text : T.surfaceSoft,
            color: ch===i ? "#fff" : T.textSoft,
            border: ch===i ? "none" : `1px solid ${T.border}`,
          }}>
            {c.icon} {c.chapter}
          </button>
        ))}
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        {THEORY[ch].articles.map((a, i) => {
          const isOpen = art === i;
          return (
            <div key={i} onClick={() => setArt(isOpen ? null : i)} style={{
              ...DS.card, padding:"12px 14px", cursor:"pointer",
              border:`1px solid ${isOpen ? T.accent : T.border}`,
              background: isOpen ? T.surface : T.surface,
              transition:"all 0.15s",
            }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                <div style={{ fontFamily:"Inter,sans-serif", fontSize:13, fontWeight:600, color:T.text }}>{a.title}</div>
                <div style={{ fontSize:16, color:T.textFaint, flexShrink:0 }}>{isOpen ? "−" : "+"}</div>
              </div>
              {isOpen && (
                <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${T.border}` }}>
                  {a.content.split("\n\n").map((para, p) => {
                    const isHeader = para.length < 50 && para === para.toUpperCase() && para.trim().length > 2;
                    return (
                      <div key={p} style={{
                        marginBottom:10,
                        fontFamily:"Inter,sans-serif",
                        fontSize: isHeader ? 10 : 13,
                        fontWeight: isHeader ? 700 : 400,
                        color: isHeader ? T.textFaint : T.textSoft,
                        lineHeight:1.8,
                        letterSpacing: isHeader ? "0.07em" : "normal",
                        whiteSpace:"pre-line",
                      }}>{para}</div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ ...DS.soft, marginTop:16 }}>
        <div style={{ ...DS.label, color:T.textFaint, marginBottom:10 }}>Stufenfunktionen</div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
          {Object.entries(FC).map(([fn, fc]) => (
            <div key={fn} style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:T.rp, background:fc.bg, border:"1px solid " + fc.bd }}>
              <div style={{ width:5, height:5, borderRadius:"50%", background:fc.bd }} />
              <div style={{ fontFamily:"Inter,sans-serif", fontSize:11, fontWeight:600, color:fc.tx }}>{fn}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

// ─── Dashboard ────────────────────────────────────────────────────────────────

function DashboardTab({ setTab, user }) {
  const name = (user.display_name || user.username).split(" ")[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Guten Morgen" : hour < 18 ? "Guten Tag" : "Guten Abend";

  const [pieces,  setPieces]  = useState([]);
  const [entries, setEntries] = useState({});
  const todayKey = (() => { const d = new Date(); return d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0"); })();

  useEffect(() => {
    getPieces().then(setPieces).catch(()=>{});
    getProgress().then(data => {
      const m = {};
      for (const [k,v] of Object.entries(data)) m[k] = v;
      setEntries(m);
    }).catch(()=>{});
  }, []);

  // Streak calculation
  const streak = (() => {
    let s = 0, d = new Date();
    while (true) {
      const k = d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0");
      const e = entries[k];
      const hasAct = e && ((e.scales||[]).length > 0 || (e.pieces||[]).length > 0);
      if (!hasAct && k !== todayKey) break;
      if (hasAct) s++;
      d.setDate(d.getDate()-1);
      if (s > 365) break;
    }
    return s;
  })();

  const weekdays = Array.from({length:7},(_,i)=>{ const d=new Date(); d.setDate(d.getDate()-6+i); return d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0"); });
  const weekPracticed = weekdays.filter(k => { const e=entries[k]; return e && ((e.scales||[]).length>0||(e.pieces||[]).length>0); }).length;

  const lastPiece = pieces.sort((a,b)=>(b.last_practiced||"").localeCompare(a.last_practiced||""))[0];

  // Coach focus: suggest based on today
  const focusSuggestions = ["II–V–I in G-Dur","I–IV–V in C-Dur","Andalusische Kadenz","VI–IV–I–V in A-Moll","Modale Harmonik: Dorisch"];
  const focus = focusSuggestions[new Date().getDay() % focusSuggestions.length];

  const statStyle = { flex:1, textAlign:"center", padding:"12px 8px", borderRadius:T.rc, background:T.surface, border:`1px solid ${T.border}` };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {/* Greeting */}
      <div>
        <div style={{ fontFamily:"'Instrument Serif',Georgia,serif", fontSize:28, color:T.text, fontStyle:"italic", lineHeight:1.2 }}>{greeting}, {name}</div>
        <div style={{ ...DS.body, marginTop:4, color:T.textSoft }}>Heute passt eine kurze Harmonie-Session.</div>
      </div>

      {/* Coach Card */}
      <div style={{ ...DS.card, background:T.text, border:"none", boxShadow:"0 16px 40px rgba(0,0,0,0.12)" }}>
        <div style={{ ...DS.label, color:"rgba(255,255,255,0.45)", marginBottom:12 }}>Coach empfiehlt heute</div>
        <div style={{ fontFamily:"'Instrument Serif',Georgia,serif", fontSize:24, color:"#fff", fontStyle:"italic", lineHeight:1.25, marginBottom:4 }}>{focus}</div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.55)", fontFamily:"Inter,sans-serif", marginBottom:18 }}>Gut für 15 Minuten · Generator bereit</div>
        <button onClick={()=>setTab("generator")} style={{ ...DS.pill, background:T.accent, color:"#fff", fontSize:13, padding:"11px 20px" }}>
          Session starten →
        </button>
      </div>

      {/* Stats row */}
      <div style={{ display:"flex", gap:8 }}>
        <div style={statStyle}>
          <div style={{ fontSize:22, fontWeight:600, color:T.text, fontFamily:"Inter,sans-serif" }}>{streak}</div>
          <div style={{ ...DS.label, color:T.textFaint, marginTop:2 }}>Streak</div>
        </div>
        <div style={statStyle}>
          <div style={{ fontSize:22, fontWeight:600, color:T.text, fontFamily:"Inter,sans-serif" }}>{weekPracticed}/7</div>
          <div style={{ ...DS.label, color:T.textFaint, marginTop:2 }}>Woche</div>
        </div>
        <div style={statStyle}>
          <div style={{ fontSize:22, fontWeight:600, color:T.text, fontFamily:"Inter,sans-serif" }}>{pieces.length}</div>
          <div style={{ ...DS.label, color:T.textFaint, marginTop:2 }}>Stücke</div>
        </div>
      </div>

      {/* Continue cards */}
      <div>
        <div style={{ ...DS.label, color:T.textFaint, marginBottom:10 }}>Weiter machen</div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {lastPiece && (
            <button onClick={()=>setTab("progress")} style={{ ...DS.card, display:"flex", alignItems:"center", gap:12, border:`1px solid ${T.border}`, cursor:"pointer", textAlign:"left", width:"100%" }}>
              <div style={{ width:38, height:38, borderRadius:12, background:T.accentSoft, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.accentStrong} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:T.text, fontFamily:"Inter,sans-serif" }}>{lastPiece.title}</div>
                <div style={{ ...DS.small, color:T.textFaint }}>Zuletzt geübt · ins Journal</div>
              </div>
              <div style={{ color:T.textFaint, fontSize:18 }}>›</div>
            </button>
          )}
          <button onClick={()=>setTab("scale")} style={{ ...DS.card, display:"flex", alignItems:"center", gap:12, border:`1px solid ${T.border}`, cursor:"pointer", textAlign:"left", width:"100%" }}>
            <div style={{ width:38, height:38, borderRadius:12, background:T.sandSoft, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8B7355" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/></svg>
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:600, color:T.text, fontFamily:"Inter,sans-serif" }}>Skalen erkunden</div>
              <div style={{ ...DS.small, color:T.textFaint }}>Quintenzirkel · Stufenakkorde</div>
            </div>
            <div style={{ color:T.textFaint, fontSize:18 }}>›</div>
          </button>
          <button onClick={()=>setTab("theory")} style={{ ...DS.card, display:"flex", alignItems:"center", gap:12, border:`1px solid ${T.border}`, cursor:"pointer", textAlign:"left", width:"100%" }}>
            <div style={{ width:38, height:38, borderRadius:12, background:T.blueSoft, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:600, color:T.text, fontFamily:"Inter,sans-serif" }}>Theorie lesen</div>
              <div style={{ ...DS.small, color:T.textFaint }}>Kompaktes Wissen für die Praxis</div>
            </div>
            <div style={{ color:T.textFaint, fontSize:18 }}>›</div>
          </button>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id:"home",      label:"Home",      icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/></svg> },
  { id:"scale",     label:"Skalen",    icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="3" x2="12" y2="9"/><line x1="12" y1="15" x2="12" y2="21"/><line x1="3" y1="12" x2="9" y2="12"/><line x1="15" y1="12" x2="21" y2="12"/></svg> },
  { id:"generator", label:"Generator", icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg> },
  { id:"map",       label:"Map",       icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2a10 10 0 100 20A10 10 0 0012 2z"/><path d="M2 12h4M18 12h4M12 2v4M12 18v4"/></svg> },
  { id:"progress",  label:"Journal",   icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="3"/><line x1="7" y1="8" x2="17" y2="8"/><line x1="7" y1="12" x2="13" y2="12"/><line x1="7" y1="16" x2="11" y2="16"/></svg> },
  { id:"theory",    label:"Theorie",   icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg> },
];

function AppInner() {
  const { user, loading } = useAuth();
  const [tab,         setTab]         = useState("home");
  const [profile,     setProfile]     = useState(false);
  const [pendingProg, setPendingProg] = useState(null);

  if (loading) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:T.bg, fontFamily:"Inter,sans-serif", color:T.textFaint, fontSize:13 }}>
      Chordsn wird geladen…
    </div>
  );

  if (!user) return <LoginScreen />;

  const navH = 64;

  return (
    <div style={{ minHeight:"100vh", background:T.bg, color:T.text, fontFamily:"Inter,sans-serif", paddingBottom:`calc(${navH}px + env(safe-area-inset-bottom, 0px))` }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Instrument+Serif:ital@0;1&display=swap" rel="stylesheet" />
      <style>{`
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        @keyframes fadeRise { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        .page-enter { animation: fadeRise 0.25s ease forwards; }
        textarea, input, select { font-family: Inter, sans-serif; }
        button:active { transform: scale(0.98); }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom:`1px solid ${T.border}`, padding:"0 16px", display:"flex", alignItems:"center", justifyContent:"space-between", height:52, position:"sticky", top:0, background:T.surface, zIndex:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:7 }}>
          <span style={{ fontFamily:"'Instrument Serif',Georgia,serif", fontSize:20, color:T.text, fontStyle:"italic" }}>Chordsn</span>
          <div style={{ width:6, height:6, borderRadius:"50%", background:T.accent }} />
        </div>
        <button onClick={() => setProfile(true)} style={{
          display:"flex", alignItems:"center", gap:7, padding:"6px 10px",
          borderRadius:T.rp, border:`1px solid ${T.border}`, background:T.surfaceSoft,
          cursor:"pointer", fontFamily:"Inter,sans-serif", fontSize:12, fontWeight:600, color:T.textSoft,
        }}>
          <div style={{ width:26, height:26, borderRadius:"50%", background:T.text, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:"#fff" }}>
            {(user.display_name || user.username).slice(0,1).toUpperCase()}
          </div>
          <span>{user.display_name || user.username}</span>
          {user.role === "admin" && <span style={{ fontSize:9, background:T.blueSoft, color:"#1565c0", padding:"1px 6px", borderRadius:T.rp, fontWeight:700 }}>ADMIN</span>}
        </button>
      </div>

      {/* Content */}
      {tab === "map"
        ? <div className="page-enter" style={{ padding:"12px 10px", maxWidth:600, margin:"0 auto" }}><HarmonyMap /></div>
        : <div className="page-enter" style={{ padding:"20px 16px", maxWidth:600, margin:"0 auto" }}>
            {tab==="home"      && <CoachTab setTab={setTab} user={user} />}
            {tab==="scale"     && <ScaleTab onOpenInGenerator={(prog) => { setPendingProg(prog); setTab("generator"); window.scrollTo(0,0); }} />}
            {tab==="generator" && <GeneratorTab pendingProg={pendingProg} onPendingConsumed={() => setPendingProg(null)} />}
            {tab==="progress"  && <ProgressTab />}
            {tab==="theory"    && <TheoryTab />}
          </div>
      }

      {/* Bottom nav */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:T.surface, borderTop:`1px solid ${T.border}`, zIndex:10 }}>
        <div style={{ display:"flex", height:navH, maxWidth:600, margin:"0 auto" }}>
          {TABS.map(t => {
            const on = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                flex:1, border:"none", background:"transparent", cursor:"pointer",
                display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
                gap:3, padding:"8px 2px",
              }}>
                <div style={{ color: on ? T.accentStrong : T.textFaint, display:"flex", alignItems:"center" }}>{t.icon}</div>
                <div style={{ fontSize:10, fontFamily:"Inter,sans-serif", fontWeight: on ? 700 : 500, color: on ? T.accentStrong : T.textFaint, letterSpacing:"0.01em" }}>{t.label}</div>
                {on && <div style={{ position:"absolute", bottom:0, width:24, height:2.5, borderRadius:T.rp, background:T.accentStrong }} />}
              </button>
            );
          })}
        </div>
        <div style={{ height:"env(safe-area-inset-bottom, 0px)", background:T.surface }} />
      </div>

      {profile && <ProfileModal onClose={() => setProfile(false)} />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
